// The top-level class to hold all functionality together.
import React, { Suspense } from 'react';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';

import { Drafty, Tinode } from 'tinode-sdk';

import Alert from '../widgets/alert.jsx';
import ContextMenu from '../widgets/context-menu.jsx';
import ForwardDialog from '../widgets/forward-dialog.jsx';
import CallIncoming from '../widgets/call-incoming.jsx';
import ChatLoadingView from '../widgets/chat-loading-view.jsx';
const PhoneCountrySelector = React.lazy(_ => import('../widgets/phone-country-selector.jsx'));

import InfoView from './info-view.jsx';
import MessagesView from './messages-view.jsx';
import ChatRoomView from './chatroom-view.jsx';
import SidepanelView from './sidepanel-view.jsx';

import { API_KEY, APP_NAME, AUTH_TIMEOUT, DEFAULT_P2P_ACCESS_MODE, DISCONNECT_GRACE_MS,
  FORWARDED_PREVIEW_LENGTH, LOGGING_ENABLED, MEDIA_BREAKPOINT, NO_CREDENTIAL_GRACE,
  SDK_SETUP_TIMEOUT, WAKE_UP_TICK, WAKE_UP_TIMEOUT } from '../config.js';
import { CALL_STATE_NONE, CALL_STATE_OUTGOING_INITATED,
         CALL_STATE_INCOMING_RECEIVED, CALL_STATE_IN_PROGRESS,
         CALL_HEAD_STARTED }  from '../constants.js';
import { base64ReEncode, makeImageUrl } from '../lib/blob-helpers.js';
import { detectServerAddress, isSecureConnection } from '../lib/host-name.js';
import { clearAdminParentMessengerToken, getAdminParentMessengerToken,
  getAdminParentMessengerTopic } from '../lib/admin-parent-auth.js';
import { bootLog, bootLogEnabled, bootWarn, credentialSnapshot,
  formatLogData } from '../lib/boot-diagnostics.js';
import { discoverJWTToken } from '../lib/jwt-auth.js';
import { NATIVE_AUTH_STATUS, settleNativeAuthFailure,
  settleNativeAuthSuccess } from '../lib/native-auth-bridge.js';
import { CHAT_ERROR_REASON, notifyChatFatalError,
  notifySessionExpired } from '../lib/native-bridge.js';
import { markOnce, PERF_MARKS } from '../lib/perf-metrics.js';
import LocalStorageUtil from '../lib/local-storage.js';
import HashNavigation from '../lib/navigation.js';
import { applyTinodeModerationPubPatch } from '../lib/tinode-moderation-pub-patch.js';
import { applyTinodeTripAutomatedReadPatch } from '../lib/tinode-trip-automated-read-patch.js';
import { checkNSFWContent, loadNSFWModel } from '../lib/nsfw-check.js';
import { secondsToTime } from '../lib/strformat.js'
import { updateFavicon } from '../lib/utils.js';

// Sound to play on message received.
const POP_SOUND = new Audio('audio/msg.m4a');

const messages = defineMessages({
  reconnect_countdown: {
    id: 'reconnect_countdown',
    defaultMessage: 'Disconnected. Reconnecting in {seconds}…',
    description: 'Message shown when an app update is available.'
  },
  reconnect_now: {
    id: 'reconnect_now',
    defaultMessage: 'Try now',
    description: 'Prompt for reconnecting now'
  },
  invalid_security_token: {
    id: 'invalid_security_token',
    defaultMessage: 'Invalid security token',
    description: 'Error message when resetting password.'
  },
  no_connection: {
    id: 'no_connection',
    defaultMessage: 'No connection',
    description: 'Warning that the user is offline.'
  },
  slow_internet_connection: {
    id: 'slow_internet_connection',
    defaultMessage: 'Slow Internet Connection',
    description: 'Warning that the user has a slow internet connection.'
  },
  code_doesnot_match: {
    id: 'code_doesnot_match',
    defaultMessage: 'Code does not match',
    description: 'Error message when the credential validation code is incorrect.'
  },
  menu_item_info: {
    id: 'menu_item_info',
    defaultMessage: 'Info',
    description: 'Show extended topic information'
  },
  menu_item_audio_call: {
    id: 'menu_item_audio_call',
    defaultMessage: 'Call',
    description: 'Start audio call'
  },
  menu_item_video_call: {
    id: 'menu_item_video_call',
    defaultMessage: 'Video call',
    description: 'Start video call'
  },
  cred_confirmed_successfully: {
    id: 'cred_confirmed_successfully',
    defaultMessage: 'Confirmed successfully',
    description: 'Notification message that the credential was successfully validated.'
  },
  password_reset_success: {
    id: 'password_reset_success',
    defaultMessage: 'Password reset successfully',
    description: 'Notification message that the password was successfully reset.'
  }
});

let wsUpgradeJWT = '';

const appendJWTToWebSocketURL = (url) => {
  if (!wsUpgradeJWT) {
    return url;
  }

  try {
    const parsed = new URL(url);
    parsed.searchParams.set('jwt', wsUpgradeJWT);
    return parsed.toString();
  } catch (_) {
    return url;
  }
};

// Resolves once `promise` settles, or after `ms` if it never does, reporting
// which happened as {value} / {error} / {timedOut}. The SDK's IndexedDB work can
// stay pending indefinitely when the database is blocked by a still-open
// connection from a previous page instance, and bootstrap must proceed anyway.
function settleWithin(promise, ms) {
  return new Promise(resolve => {
    let timer = null;
    let done = false;
    const finish = outcome => {
      if (done) {
        return;
      }
      done = true;
      clearTimeout(timer);
      resolve(outcome);
    };
    timer = setTimeout(() => finish({timedOut: true}), ms);
    promise.then(value => finish({value}), error => finish({error}));
  });
}

class JWTWebSocketProvider {
  constructor(url, protocols) {
    const wsURL = appendJWTToWebSocketURL(url);
    if (protocols) {
      return new WebSocket(wsURL, protocols);
    }
    return new WebSocket(wsURL);
  }
}

class TinodeWeb extends React.Component {
  constructor(props) {
    super(props);

    this.selfRef = React.createRef();
    this.pendingJwtLogin = false;
    this.authTimeoutTimer = null;
    // While set, the embed mounted without credentials and is still waiting for
    // the native host to deliver a JWT.
    this.noCredentialTimer = null;
    // While set, reconnect UI is suppressed and ErrorPanel shows Slow Connection.
    this.disconnectGraceTimer = null;
    this.nativeErrorSignaled = false;
    this.sessionExpiredSignaled = false;

    this.state = this.getBlankState();

    this.handleResize = this.handleResize.bind(this);
    this.handleHashRoute = this.handleHashRoute.bind(this);
    this.handleOnline = this.handleOnline.bind(this);
    this.handleVisibilityEvent = this.handleVisibilityEvent.bind(this);
    this.handleError = this.handleError.bind(this);
    this.handleLoginRequest = this.handleLoginRequest.bind(this);
    this.handleNativeJWTReady = this.handleNativeJWTReady.bind(this);
    this.handlePersistenceChange = this.handlePersistenceChange.bind(this);
    this.handleConnected = this.handleConnected.bind(this);
    this.handleAutoreconnectIteration = this.handleAutoreconnectIteration.bind(this);
    this.doLogin = this.doLogin.bind(this);
    this.handleLoginSuccessful = this.handleLoginSuccessful.bind(this);
    this.handleDisconnect = this.handleDisconnect.bind(this);
    this.tnMeMetaDesc = this.tnMeMetaDesc.bind(this);
    this.tnMeContactUpdate = this.tnMeContactUpdate.bind(this);
    this.tnMeSubsUpdated = this.tnMeSubsUpdated.bind(this);
    this.resetContactList = this.resetContactList.bind(this);
    this.tnInitFind = this.tnInitFind.bind(this);
    this.tnFndSubsUpdated = this.tnFndSubsUpdated.bind(this);
    this.handleSearchContacts = this.handleSearchContacts.bind(this);
    this.handleTopicSelected = this.handleTopicSelected.bind(this);
    this.handleHideMessagesView = this.handleHideMessagesView.bind(this);
    this.handleSendMessage = this.handleSendMessage.bind(this);
    this.handleNewChatInvitation = this.handleNewChatInvitation.bind(this);
    this.handleNewAccount = this.handleNewAccount.bind(this);
    this.handleNewAccountRequest = this.handleNewAccountRequest.bind(this);
    this.handleUpdatePasswordRequest = this.handleUpdatePasswordRequest.bind(this);
    this.handleUpdateAccountTagsRequest = this.handleUpdateAccountTagsRequest.bind(this);
    this.handleToggleIncognitoMode = this.handleToggleIncognitoMode.bind(this);
    this.handleSettings = this.handleSettings.bind(this);
    this.handleGlobalSettings = this.handleGlobalSettings.bind(this);
    this.handleShowArchive = this.handleShowArchive.bind(this);
    this.handleShowBlocked = this.handleShowBlocked.bind(this);
    this.handleToggleMessageSounds = this.handleToggleMessageSounds.bind(this);
    this.handleCredAdd = this.handleCredAdd.bind(this);
    this.handleCredDelete = this.handleCredDelete.bind(this);
    this.handleCredConfirm = this.handleCredConfirm.bind(this);
    this.handleSidepanelCancel = this.handleSidepanelCancel.bind(this);
    this.handleStartTopicRequest = this.handleStartTopicRequest.bind(this);
    this.handleNewTopicCreated = this.handleNewTopicCreated.bind(this);
    this.handleTopicUpdateRequest = this.handleTopicUpdateRequest.bind(this);
    this.handleUnarchive = this.handleUnarchive.bind(this);
    this.handleChangePermissions = this.handleChangePermissions.bind(this);
    this.handleTagsUpdateRequest = this.handleTagsUpdateRequest.bind(this);
    this.handleLogout = this.handleLogout.bind(this);
    this.handleDeleteAccount = this.handleDeleteAccount.bind(this);
    this.handleDeleteTopicRequest = this.handleDeleteTopicRequest.bind(this);
    this.handleDeleteMessagesRequest = this.handleDeleteMessagesRequest.bind(this);
    this.handleLeaveUnsubRequest = this.handleLeaveUnsubRequest.bind(this);
    this.handleBlockTopicRequest = this.handleBlockTopicRequest.bind(this);
    this.handleReportTopic = this.handleReportTopic.bind(this);
    this.handleShowContextMenu = this.handleShowContextMenu.bind(this);
    this.defaultTopicContextMenu = this.defaultTopicContextMenu.bind(this);
    this.handleHideContextMenu = this.handleHideContextMenu.bind(this);
    this.handleShowAlert = this.handleShowAlert.bind(this);
    this.handleShowInfoView = this.handleShowInfoView.bind(this);
    this.handleMemberUpdateRequest = this.handleMemberUpdateRequest.bind(this);
    this.handleValidateCredentialsRequest = this.handleValidateCredentialsRequest.bind(this);
    this.handlePasswordResetRequest = this.handlePasswordResetRequest.bind(this);
    this.handleResetPassword = this.handleResetPassword.bind(this);
    this.handleContextMenuAction = this.handleContextMenuAction.bind(this);
    this.handleShowCountrySelector =  this.handleShowCountrySelector.bind(this);

    this.handleShowForwardDialog = this.handleShowForwardDialog.bind(this);
    this.handleHideForwardDialog = this.handleHideForwardDialog.bind(this);

    this.handleStartVideoCall = this.handleStartVideoCall.bind(this);
    this.handleStartAudioCall = this.handleStartAudioCall.bind(this);
    this.handleInfoMessage = this.handleInfoMessage.bind(this);
    this.handleDataMessage = this.handleDataMessage.bind(this);
    this.handleCallClose = this.handleCallClose.bind(this);

    this.handleCallInvite = this.handleCallInvite.bind(this);
    this.handleCallRinging = this.handleCallRinging.bind(this);
    this.handleCallHangup = this.handleCallHangup.bind(this);
    this.handleCallSendOffer = this.handleCallSendOffer.bind(this);
    this.handleCallIceCandidate = this.handleCallIceCandidate.bind(this);
    this.handleCallSendAnswer = this.handleCallSendAnswer.bind(this);

    this.handleCallAccept = this.handleCallAccept.bind(this);

    this.sendMessageToTopic = this.sendMessageToTopic.bind(this);

    // FIXME: this is unused.
    this.callTimeoutTimer = null;
  }

  getBlankState() {
    const settings = LocalStorageUtil.getObject('settings') || {};
    const route = TinodeWeb.parseInitialRoute();
    const storedToken = LocalStorageUtil.getObject('auth-token');
    const persist = !!LocalStorageUtil.getObject('keep-logged-in') &&
      TinodeWeb.hasValidStoredAuthToken(storedToken);

    return {
      connected: false,
      // Connected and subscribed to 'me'
      ready: false,
      tinodeReady: false,
      // Try to re-login on new connection only after credentials are confirmed in componentDidMount.
      autoLogin: false,
      transport: settings.transport || null,
      serverAddress: settings.serverAddress || detectServerAddress(),
      secureConnection: settings.secureConnection === undefined ?
        isSecureConnection() : settings.secureConnection,
      serverVersion: "no connection",
      // "On" is the default, so saving the "off" state.
      messageSounds: !settings.messageSoundsOff,
      incognitoMode: false,

      applicationVisible: !document.hidden,

      errorText: '',
      errorLevel: null,
      errorAction: undefined,
      errorActionText: null,
      errorId: null,

      sidePanelSelected: route.isCaxDaxRoute ? route.routeType : '',
      sidePanelTitle: null,
      sidePanelAvatar: null,
      myTrustedBadges: [],
      myTrustedBadgesReady: false,
      loadSpinnerVisible: false,
      // Start cax/dax routes in the "authenticating" state when a token is
      // available (localStorage, bridge, postMessage, or URL) so the loading
      // view is shown from the first render instead of briefly flashing
      // "No Chat" before the async auth attempt in componentDidMount kicks in.
      caxDaxPendingAuth: route.isCaxDaxRoute && route.hasAuth,
      authFailed: false,

      login: '',
      password: '',
      persist: persist,
      myUserId: null,
      liveConnection: navigator.onLine,
      topicSelected: route.validTopic || '',
      topicSelectedOnline: false,
      topicSelectedAcs: null,
      newTopicParams: null,
      loginDisabled: false,
      displayMobile: (window.innerWidth <= MEDIA_BREAKPOINT),
      infoPanel: undefined,
      mobilePanel: route.isCaxDaxRoute && route.validTopic ? 'topic-view' : 'sidepanel',

      // Video calls.
      callTopic: undefined,
      callState: CALL_STATE_NONE,
      callAudioOnly: undefined,
      // If true, call state should be transitioned to CALL_STATE_IN_PROGRESS upon
      // switching to the call topic.
      callShouldStart: false,

      contextMenuVisible: false,
      contextMenuBounds: null,
      contextMenuClickAt: null,
      contextMenuParams: null,
      contextMenuItems: [],

      forwardDialogVisible: false,
      forwardMessage: null,

      // Modal alert dialog.
      alertVisible: false,
      alertParams: {},

      // Chats as shown in the ContactsView
      chatList: [],
      // Contacts returned by a search query.
      searchResults: [],
      // Merged results of a search query and p2p chats.
      searchableContacts: [],
      // Credential validation.
      reqCredMethod: undefined,
      credMethod: undefined,
      credCode: undefined,
      credToken: undefined,
      chatRoomMode: route.isCaxDaxRoute,
      // Topic to go to after login.
      requestedTopic: route.requestedTopic,
      requestedHash: route.isCaxDaxRoute ? route.requestedHash : null,
    };
  }

  static getCaxDaxRouteType(path) {
    const routeType = path?.[0];
    return routeType === 'cax' || routeType === 'dax' ? routeType : null;
  }

  static applyCaxDaxTheme(routeType) {
    const html = document.documentElement;
    if (routeType === 'cax') {
      html.classList.add('theme-light');
      html.classList.remove('theme-dark');
    } else if (routeType === 'dax') {
      html.classList.add('theme-dark');
      html.classList.remove('theme-light');
    }
  }

  static isEmbedRouteHash(hash) {
    return !!TinodeWeb.getCaxDaxRouteType(HashNavigation.parseUrlHash(hash || '').path);
  }

  // Prefer the live browser hash for cax/dax embeds so reconnect does not restore a stale requestedHash.
  static resolveEmbedHash(requestedHash) {
    const currentHash = globalThis.location.hash;
    if (TinodeWeb.isEmbedRouteHash(currentHash)) {
      return currentHash;
    }
    if (TinodeWeb.isEmbedRouteHash(requestedHash)) {
      return requestedHash;
    }
    return requestedHash || '';
  }

  static hasValidStoredAuthToken(token) {
    if (!token?.token) {
      return false;
    }
    if (!token.expires) {
      return true;
    }
    const expires = token.expires instanceof Date ? token.expires : new Date(token.expires);
    if (Number.isNaN(expires.getTime())) {
      return true;
    }
    // Ignore tokens that expire within the next minute.
    return expires.getTime() > Date.now() + 60_000;
  }

  // Remove saved login when the stored session token is missing or expired.
  static clearSavedLogin() {
    localStorage.removeItem('auth-token');
    localStorage.removeItem('keep-logged-in');
  }

  // True when a JWT may arrive from bridge or postMessage (without consuming it).
  static hasPendingJwt() {
    if ((globalThis.__ANGKAS_JWT__ || '').trim()) {
      return true;
    }
    return !!getAdminParentMessengerToken();
  }

  static parseInitialRoute() {
    const parsedNav = HashNavigation.parseUrlHash(window.location.hash);
    const routeType = TinodeWeb.getCaxDaxRouteType(parsedNav.path);
    const isCaxDaxRoute = ['cax', 'dax'].includes(routeType);
    const storedToken = LocalStorageUtil.getObject('auth-token');
    const hasStoredAuth = TinodeWeb.hasValidStoredAuthToken(storedToken);
    // Loading UI: localStorage first, then any pending JWT source.
    const hasAuth = hasStoredAuth || TinodeWeb.hasPendingJwt();
    const topicName = parsedNav.path?.[1];
    const validTopic = topicName && Tinode.topicType(topicName) ? topicName : null;

    if (isCaxDaxRoute) {
      TinodeWeb.applyCaxDaxTheme(routeType);
    }

    // hasAuth decides whether the embed starts in the "authenticating" state.
    // When it is false on a cax/dax route the very first paint is already the
    // blank screen, and only a late `angkas-jwt-ready` event can recover it.
    bootLog('route:initial', {
      isCaxDaxRoute,
      routeType,
      hasAuth,
      hasStoredAuth,
      hasPendingJwt: TinodeWeb.hasPendingJwt(),
      validTopic,
      ...credentialSnapshot(),
    });
    if (isCaxDaxRoute && !hasAuth) {
      bootWarn('route:initial NO CREDENTIALS AT MOUNT — embed will paint blank ' +
        'unless a JWT arrives and dispatches the angkas-jwt-ready event');
    }

    return {
      isCaxDaxRoute,
      hasAuth,
      validTopic,
      routeType,
      requestedHash: window.location.hash,
      requestedTopic: validTopic || undefined,
    };
  }

  componentDidMount() {
    // Unregister any service worker from the previous Firebase/PWA build so stale
    // caches and push handlers do not keep running.
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(regs => regs.forEach(r => r.unregister()));
    }

    window.addEventListener('resize', this.handleResize);
    this.handleOnlineOn = _ => { this.handleOnline(true); }
    window.addEventListener('online', this.handleOnlineOn);
    this.handleOnlineOff = _ => { this.handleOnline(false); }
    window.addEventListener('offline', this.handleOnlineOff);
    window.addEventListener('hashchange', this.handleHashRoute);

    // Detect if computer just woke up from sleep. Checks how much time has passed since the last tick.
    // If too much time has passed then the computer was asleep.
    this.lastWakeUpCheck = new Date().getTime();
    this.wakeUpTicker = setInterval(_ => {
      const now = new Date().getTime();
      if (now - this.lastWakeUpCheck > WAKE_UP_TIMEOUT) {
        // Trigger reconnect.
        this.handleOnlineOn();
      }
      this.lastWakeUpCheck = now;
    }, WAKE_UP_TICK);

    // Window/tab visible or invisible for pausing timers.
    document.addEventListener('visibilitychange', this.handleVisibilityEvent);
    globalThis.addEventListener('angkas-jwt-ready', this.handleNativeJWTReady);

    this.setState({
      viewportWidth: document.documentElement.clientWidth,
      viewportHeight: document.documentElement.clientHeight
    });

    bootLog('mount:start', {persist: this.state.persist, chatRoomMode: this.state.chatRoomMode,
      caxDaxPendingAuth: this.state.caxDaxPendingAuth, topicSelected: this.state.topicSelected});

    // The SDK invokes its setup callback only after IndexedDB has been opened
    // (persist) or deleted (no persist), and it swallows a rejection from either
    // one. A blocked IndexedDB — which happens when a previous page instance has
    // not released its connection yet, as when navigating back and forth in the
    // WebView — would leave that callback pending forever, and with it everything
    // below: no login, no hash routing, and no AUTH_TIMEOUT backstop to report
    // the failure, so the embed stays blank with no way out. Cap the wait and
    // continue without the persistent cache, which only costs a cold fetch.
    const setupPromise = new Promise(resolve => {
      this.tinode = TinodeWeb.tnSetup(this.state.serverAddress, isSecureConnection(), this.state.transport,
        this.props.intl.locale, this.state.persist, resolve);
      this.tinode.onConnect = this.handleConnected;
      this.tinode.onDisconnect = this.handleDisconnect;
      this.tinode.onAutoreconnectIteration = this.handleAutoreconnectIteration;
      this.tinode.onInfoMessage = this.handleInfoMessage;
      this.tinode.onDataMessage = this.handleDataMessage;
      this.setState({tinodeReady: true});
    });

    settleWithin(setupPromise, SDK_SETUP_TIMEOUT).then(outcome => {
      if (outcome.timedOut) {
        bootWarn('mount:tinode-setup TIMED OUT — SDK setup callback did not fire within ' +
          `${SDK_SETUP_TIMEOUT}ms (IndexedDB "tinode-web" is likely blocked by another page ` +
          'instance); continuing without the persistent cache.', {persist: this.state.persist});
      } else {
        bootLog('mount:tinode-setup done', {err: outcome.value?.message || outcome.value || null});
      }

      const parsedNav = HashNavigation.parseUrlHash(window.location.hash);
      const isCaxDaxRoute = parsedNav.path?.length > 0 && ['cax', 'dax'].includes(parsedNav.path[0]);
      const requestedHash = window.location.hash;

      const rawToken = LocalStorageUtil.getObject('auth-token');
      const hasSavedLogin = !!LocalStorageUtil.getObject('keep-logged-in') || !!rawToken;
      const mustDropSavedLogin = hasSavedLogin && !TinodeWeb.hasValidStoredAuthToken(rawToken);
      bootLog('mount:saved-login', {hasSavedLogin, mustDropSavedLogin,
        storedTokenExpires: rawToken?.expires || null});

      // Drop the saved login however clearStorage() ends, including not at all:
      // a rejection or a hang here must not stall the rest of the bootstrap.
      const readyPromise = mustDropSavedLogin ?
        settleWithin(this.tinode.clearStorage(), SDK_SETUP_TIMEOUT).then(cleared => {
          if (cleared.timedOut) {
            bootWarn('mount:clearStorage timed out — continuing without clearing the cache');
          } else if (cleared.error) {
            // "blocked" here means another page instance still holds IndexedDB open.
            bootWarn('mount:clearStorage failed',
              {message: cleared.error?.message || String(cleared.error)});
          } else {
            bootLog('mount:clearStorage ok');
          }
          TinodeWeb.clearSavedLogin();
        }) :
        Promise.resolve();

      return readyPromise.then(_ => {
      bootLog('mount:auth-stage', {isCaxDaxRoute, requestedHash, ...credentialSnapshot()});
      this.resetContactList();

      const token = TinodeWeb.hasValidStoredAuthToken(rawToken) ? rawToken : undefined;
      // A late-arriving JWT (admin portal / native bridge) may have already started
      // login via handleNativeJWTReady. If so, leave that attempt untouched: a second
      // connect() would tear down the first socket mid-handshake, and the clearAuthAttempt()
      // in the else branch would cancel the in-flight login (autoLogin=false) so it never
      // completes. Only drive auto-login here when no JWT login is already pending.
      if (this.pendingJwtLogin) {
        bootLog('mount:auth skipped — a late JWT login is already in flight');
      }
      if (!this.pendingJwtLogin) {
        // Auth priority:
        // 1) valid localStorage session token
        // 2) JWT from webview bridge, then admin postMessage
        if (token) {
          bootLog('mount:auth branch=stored-session-token');
          const loginState = {
            autoLogin: true,
            loadSpinnerVisible: true,
            authFailed: false,
          };
          if (isCaxDaxRoute) {
            loginState.caxDaxPendingAuth = true;
          }
          this.setState(loginState);
          this.startAuthTimeout();
          // When reading from storage, date is returned as string.
          token.expires = new Date(token.expires);
          this.tinode.setAuthToken(token);
          this.tinode.connect().catch(err => {
            bootWarn('mount:auth connect failed (stored token)', {message: err?.message});
            this.clearAuthAttempt();
            this.handleError(err.message, 'err');
            this.setState({authFailed: true});
            this.signalChatLoadFailure(CHAT_ERROR_REASON.CONNECTION_FAILED);
          });
        } else {
          const jwtToken = (discoverJWTToken() || '').trim();
          if (jwtToken) {
            bootLog('mount:auth branch=jwt', {length: jwtToken.length});
            const loginState = {
              autoLogin: true,
              loadSpinnerVisible: true,
              authFailed: false,
              jwtToken,
            };
            if (isCaxDaxRoute) {
              loginState.caxDaxPendingAuth = true;
            }
            this.setState(loginState);
            this.startAuthTimeout();
            this.tinode.connect().catch(err => {
              bootWarn('mount:auth connect failed (jwt)', {message: err?.message});
              this.clearAuthAttempt();
              this.handleError(err.message, 'err');
              this.setState({authFailed: true});
              this.signalChatLoadFailure(CHAT_ERROR_REASON.CONNECTION_FAILED);
            });
          } else {
            // Nothing is retried from here unless an `angkas-jwt-ready` event is
            // dispatched after the JWT is injected, which is what the grace below
            // waits for.
            bootWarn('mount:auth branch=NONE — no session token and no JWT available. ' +
              'Embed renders blank until one arrives.', credentialSnapshot());
            this.clearAuthAttempt();
            if (this.state.chatRoomMode) {
              this.setState({authFailed: true});
              this.startNoCredentialGrace();
            }
          }
        }
      }

      // Maybe navigate to home screen. Keep cax/dax hash intact to avoid UI flicker.
      if (!['cred', 'reset'].includes(parsedNav.path[0])) {
        // Fall back to the admin-portal-supplied topic when the URL hash has no topic.
        const parentTopic = getAdminParentMessengerTopic();
        const validParentTopic = parentTopic && Tinode.topicType(parentTopic) ? parentTopic : undefined;
        const mountState = {
          requestedTopic: parsedNav.path[1] || validParentTopic,
        };
        if (isCaxDaxRoute) {
          mountState.requestedHash = requestedHash;
        }
        if (!isCaxDaxRoute) {
          mountState.sidePanelSelected = '';
          const path = parsedNav.params?.cred_done ?
            HashNavigation.addUrlParam('', 'cred_done', parsedNav.params.cred_done) :
            '';
          HashNavigation.navigateTo(path);
        }
        this.setState(mountState);
      }

      this.handleHashRoute();
      bootLog('mount:complete');
      });
    }).catch(err => {
      bootWarn('mount:chain FAILED — bootstrap aborted, page will stay blank',
        {message: err?.message || String(err)});
    });
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('hashchange', this.handleHashRoute);
    window.removeEventListener('online', this.handleOnlineOn);
    window.removeEventListener('offline', this.handleOnlineOff);
    document.removeEventListener('visibilitychange', this.handleVisibilityEvent);
    globalThis.removeEventListener('angkas-jwt-ready', this.handleNativeJWTReady);

    clearInterval(this.wakeUpTicker);
    this.clearAuthTimeout();
    this.clearNoCredentialGrace();
    this.clearDisconnectGraceTimer();
  }

  // Setup transport (usually websocket) and server address. This will terminate connection with the server.
  static tnSetup(serverAddress, secureConnection, transport, locale, persistentCache, onSetupCompleted) {
    Tinode.setNetworkProviders(JWTWebSocketProvider, XMLHttpRequest);
    const tinode = new Tinode({appName: APP_NAME, host: serverAddress, apiKey: API_KEY, transport: transport,
      secure: secureConnection, persist: persistentCache}, onSetupCompleted);
    tinode.setHumanLanguage(locale);
    tinode.enableLogging(LOGGING_ENABLED, true);
    applyTinodeModerationPubPatch(tinode);
    applyTinodeTripAutomatedReadPatch(tinode);
    return tinode;
  }

  handleResize() {
    const mobile = document.documentElement.clientWidth <= MEDIA_BREAKPOINT;
    this.setState({
      viewportWidth: document.documentElement.clientWidth,
      viewportHeight: document.documentElement.clientHeight
    });
    if (this.state.displayMobile != mobile) {
      this.setState({displayMobile: mobile});
    }
  }

  // Handle for hash navigation (hashchange) event: update state.
  handleHashRoute() {
    const hash = HashNavigation.parseUrlHash(window.location.hash);
    // Start with panel parameters.
    const newState = {
      infoPanel: hash.params.info,
      newTopicTabSelected: hash.params.tab,
      chatRoomMode: false,
    };

    if (this.state.myUserId || (hash.path && hash.path.length > 0)) {
      const caxDaxRouteType = TinodeWeb.getCaxDaxRouteType(hash.path);
      const isDaxOrCaxRoute = !!caxDaxRouteType;
      if (caxDaxRouteType) {
        newState.chatRoomMode = true;
        TinodeWeb.applyCaxDaxTheme(caxDaxRouteType);
        newState.requestedHash = globalThis.location.hash;
      }
      if (['settings','edit','notif','security','support','general','crop',
          'cred','reset','newtpk','archive','blocked','contacts','cax','dax',''].includes(hash.path[0])) {
        newState.sidePanelSelected = hash.path[0];
      } else {
        console.warn("Unknown sidepanel view", hash.path[0]);
      }

      // Topic for MessagesView selector.
      let topicName = hash.path[1] || null;

      if (topicName != this.state.topicSelected) {
        if (!Tinode.topicType(topicName)) {
          // Clear invalid topic name and hide messages view on mobile.
          topicName = null;
          newState.mobilePanel = isDaxOrCaxRoute ? 'topic-view' : 'sidepanel';
        } else {
          // Topic valid: show messages view on mobile.
          newState.mobilePanel = 'topic-view';
        }
        Object.assign(newState, {
          topicSelected: topicName,
          topicSelectedAcs: this.tinode.getTopicAccessMode(topicName),
          forwardMessage: null
        });
      }
    } else {
      // Empty hashpath
      Object.assign(newState, {sidePanelSelected: '', topicSelected: null});
      if (!this.state.myUserId && this.state.requestedHash) {
        const caxDaxRouteType = TinodeWeb.getCaxDaxRouteType(
          HashNavigation.parseUrlHash(this.state.requestedHash).path);
        if (caxDaxRouteType) {
          newState.chatRoomMode = true;
          TinodeWeb.applyCaxDaxTheme(caxDaxRouteType);
        }
      }
    }

    // Save credential validation parameters, if available.
    if (hash.params.method) {
      newState.credMethod = hash.params.method;
    }
    if (hash.params.code) {
      newState.credCode = hash.params.code;
    }
    if (hash.params.token) {
      newState.credToken = hash.params.token;
    }

    // Show a message if validation was successful.
    if (hash.params.cred_done) {
      Object.assign(newState,
        TinodeWeb.stateForError(this.props.intl.formatMessage(messages.cred_confirmed_successfully), 'info'));
    }

    // chatRoomMode defaults to false on every hash change and is only restored
    // for a cax/dax path, so a hash that loses its path drops the embed out of
    // chat-room mode; topicSelected going null renders an empty #topic-view.
    bootLog('route:hashchange', {
      path: hash.path,
      chatRoomModeFrom: this.state.chatRoomMode,
      chatRoomModeTo: newState.chatRoomMode,
      topicSelectedFrom: this.state.topicSelected,
      topicSelectedTo: newState.topicSelected,
      topicSelectedChanged: 'topicSelected' in newState,
      sidePanelSelected: newState.sidePanelSelected,
      mobilePanel: newState.mobilePanel,
      myUserId: this.state.myUserId,
      requestedHash: this.state.requestedHash,
    });

    this.setState(newState);
  }

  handleOnline(online) {
    if (online) {
      this.handleError();
      clearInterval(this.reconnectCountdown);
      this.tinode.reconnect();
    } else if (!this.disconnectGraceTimer) {
      this.handleError(this.props.intl.formatMessage(messages.no_connection), 'warn');
    }
    this.setState({liveConnection: online});
  }

  handleVisibilityEvent() {
    this.setState({applicationVisible: !document.hidden});
  }

  static stateForError(err, level, action, actionText) {
    return {
      errorText: err,
      errorLevel: level,
      errorAction: action,
      errorActionText: actionText,
      callShouldStart: false,
    };
  }

  handleError(err, level, action, actionText) {
    // Keep Slow Connection on ErrorPanel during disconnect grace.
    if (err && this.disconnectGraceTimer) {
      return;
    }
    // SDK network errors: "timeout (504)" / "Connection failed (503)".
    if (err && (err === 'timeout (504)' || err === 'Connection failed (503)')) {
      this.showSlowConnectionBanner();
    } else {
      this.setState({
        ...TinodeWeb.stateForError(err, level, action, actionText),
        errorId: null,
      });
    }
  }

  showSlowConnectionBanner() {
    const text = this.props.intl.formatMessage(messages.slow_internet_connection);
    this.setState({
      ...TinodeWeb.stateForError(text, 'warn'),
      errorId: messages.slow_internet_connection.id,
    });
  }

  // User chose Settings menu item.
  handleSettings() {
    this.handleError();
  }

  isAuthInProgress() {
    return !this.state.myUserId && (
      this.pendingJwtLogin ||
      this.state.loadSpinnerVisible ||
      this.state.caxDaxPendingAuth
    );
  }

  // True when the session is usable right now. myUserId survives an expired or
  // dropped session because only handleLogout clears it, so the SDK's own auth
  // flag is the authority on whether credentials still work.
  hasLiveSession() {
    return !!this.state.myUserId && !!this.tinode?.isAuthenticated();
  }

  startAuthTimeout() {
    this.clearAuthTimeout();
    // An actual attempt is starting, so it owns the outcome from here on.
    this.clearNoCredentialGrace();
    if (this.state.myUserId) {
      return;
    }
    // New attempt: allow a fresh fatal_error signal to reach the native host.
    this.nativeErrorSignaled = false;
    this.authTimeoutTimer = setTimeout(() => this.handleAuthTimeout(), AUTH_TIMEOUT);
  }

  // Notify the native host once per attempt that chat failed to load.
  signalChatLoadFailure(reason) {
    if (this.state.myUserId) {
      // An established session was rejected rather than a load failing. The host
      // can recover this by supplying a fresh JWT, so it gets the actionable
      // signal instead of the terminal one.
      if (reason === CHAT_ERROR_REASON.AUTH_REJECTED) {
        this.signalSessionExpired();
      }
      return;
    }
    // Settled ahead of the once-per-attempt guard so a pending AngkasAuth.login()
    // still gets its auth_result on a later failure in the same attempt.
    settleNativeAuthFailure(reason);
    if (this.nativeErrorSignaled) {
      return;
    }
    this.nativeErrorSignaled = true;
    notifyChatFatalError(reason);
  }

  // Ask the native host once for a fresh JWT. Re-armed on the next successful
  // login so a later expiry is reported again.
  signalSessionExpired() {
    settleNativeAuthFailure(CHAT_ERROR_REASON.AUTH_REJECTED);
    if (this.sessionExpiredSignaled) {
      return;
    }
    this.sessionExpiredSignaled = true;
    bootWarn('auth:session expired — requesting a fresh JWT from the native host');
    notifySessionExpired();
  }

  clearAuthTimeout() {
    if (this.authTimeoutTimer) {
      clearTimeout(this.authTimeoutTimer);
      this.authTimeoutTimer = null;
    }
  }

  // Mounting with no credential is the normal case for a WebView embed: the
  // native host calls AngkasAuth.login() only once the page is up, so reporting
  // a failure right away would be a false alarm for every cold start. Wait out
  // the handover instead and report only if no token ever arrives.
  startNoCredentialGrace() {
    this.clearNoCredentialGrace();
    this.noCredentialTimer = setTimeout(() => {
      this.noCredentialTimer = null;
      if (this.state.myUserId || this.isAuthInProgress()) {
        return;
      }
      bootWarn('auth:no-credential grace expired — no JWT was handed over',
        {graceMs: NO_CREDENTIAL_GRACE, ...credentialSnapshot()});
      this.signalChatLoadFailure(CHAT_ERROR_REASON.AUTH_REJECTED);
    }, NO_CREDENTIAL_GRACE);
  }

  clearNoCredentialGrace() {
    if (this.noCredentialTimer) {
      clearTimeout(this.noCredentialTimer);
      this.noCredentialTimer = null;
    }
  }

  clearAuthAttempt(keepAutoLogin = false, keepAuthTimeout = false) {
    // Retryable (5xx) auto-login keeps the AUTH_TIMEOUT backstop so a persistent
    // failure still fires handleAuthTimeout() → fatal_error, never a silent blank.
    if (!keepAuthTimeout) {
      this.clearAuthTimeout();
    }
    this.pendingJwtLogin = false;
    const patch = {
      loadSpinnerVisible: false,
      caxDaxPendingAuth: false,
      loginDisabled: false,
    };
    if (!keepAutoLogin) {
      patch.autoLogin = false;
    }
    this.setState(patch);
  }

  handleAuthTimeout() {
    this.authTimeoutTimer = null;
    if (this.state.myUserId) {
      return;
    }
    bootWarn('auth:timeout', {connected: this.state.connected, pendingJwtLogin: this.pendingJwtLogin});
    console.warn('Auth attempt timed out');
    this.clearAuthAttempt();
    this.setState({authFailed: true});
    this.signalChatLoadFailure(CHAT_ERROR_REASON.CONNECTION_TIMEOUT);
  }

  handleNativeJWTReady() {
    bootLog('auth:angkas-jwt-ready received', {myUserId: this.state.myUserId,
      pendingJwtLogin: this.pendingJwtLogin, hasJwtInState: !!this.state.jwtToken,
      sdkAuthenticated: !!this.tinode?.isAuthenticated()});
    if (this.hasLiveSession()) {
      settleNativeAuthSuccess(NATIVE_AUTH_STATUS.ALREADY_AUTHENTICATED);
      return;
    }
    // state.jwtToken is deliberately not a guard here: it holds the token of the
    // session being replaced, so treating it as "attempt in flight" would block
    // every re-authentication.
    if (this.pendingJwtLogin || this.isAuthInProgress()) {
      // An attempt is already running and will report the outcome for us.
      return;
    }
    // Prefer a valid localStorage session over a late-arriving JWT. Skipped when
    // recovering a dead session (myUserId set, SDK not authenticated), because
    // then the stored token is the credential that just stopped working.
    const storedToken = LocalStorageUtil.getObject('auth-token');
    if (!this.state.myUserId && TinodeWeb.hasValidStoredAuthToken(storedToken)) {
      bootLog('auth:angkas-jwt-ready ignored — valid stored session takes priority');
      return;
    }
    const jwtToken = discoverJWTToken();
    bootLog('auth:late-jwt lookup', {found: !!jwtToken});
    if (jwtToken) {
      // Load the user into the topic the admin portal requested (if any) after login completes.
      const parentTopic = getAdminParentMessengerTopic();
      const requestedTopic = parentTopic && Tinode.topicType(parentTopic) ? parentTopic : undefined;
      if (requestedTopic) {
        this.setState({requestedTopic});
      }
      this.triggerAutoLogin(jwtToken);
    }
  }

  triggerAutoLogin(jwtToken) {
    if (!jwtToken || this.pendingJwtLogin || this.hasLiveSession()) {
      return;
    }
    this.pendingJwtLogin = true;
    this.handleLoginRequest(jwtToken);
  }

  // A JWT handed over while an attempt was already running is parked rather
  // than used, because handleNativeJWTReady defers to the attempt in flight. It
  // is a newer credential than the one that just failed, so spend it before
  // telling the host the load failed. Returns true when a retry was started, in
  // which case the caller must not report the failure.
  retryWithPendingJwt(usedToken) {
    if (this.state.myUserId || this.pendingJwtLogin) {
      return false;
    }
    // Consuming the token here is also what bounds the retry: a second failure
    // finds nothing left to try, or finds the same token and gives up below.
    const jwtToken = discoverJWTToken();
    if (!jwtToken || jwtToken === usedToken) {
      return false;
    }
    bootLog('auth:retry with the JWT that arrived during the failed attempt',
      {length: jwtToken.length});
    this.triggerAutoLogin(jwtToken);
    return true;
  }

  // Begin JWT authentication triggered by the native bridge or admin portal.
  handleLoginRequest(jwtToken) {
    this.setState({
      loginDisabled: true,
      loadSpinnerVisible: true,
      autoLogin: true,
      jwtToken: jwtToken,
      authFailed: false,
    });
    this.startAuthTimeout();
    this.handleError('', null);

    if (this.tinode.isConnected()) {
      this.doLogin(jwtToken, {meth: this.state.credMethod, resp: this.state.credCode});
    } else {
      this.tinode.connect().catch(err => {
        this.clearAuthAttempt();
        this.handleError(err.message, 'err');
        this.setState({authFailed: true});
        this.signalChatLoadFailure(CHAT_ERROR_REASON.CONNECTION_FAILED);
      });
    }

  }

  // Enable or disable saving the password and IndexedDB.
  handlePersistenceChange(persist) {
    if (persist) {
      this.tinode.initStorage().then(_ => {
        LocalStorageUtil.setObject('keep-logged-in', true);
        this.setState({persist: true});
      });
    } else {
      this.tinode.clearStorage().then(_ => {
        LocalStorageUtil.setObject('keep-logged-in', false);
        this.setState({persist: false});
      });
    }
  }

  clearDisconnectGraceTimer() {
    if (this.disconnectGraceTimer) {
      clearTimeout(this.disconnectGraceTimer);
      this.disconnectGraceTimer = null;
    }
  }

  // Connection succeeded.
  handleConnected() {
    bootLog('net:connected', {autoLogin: this.state.autoLogin, myUserId: this.state.myUserId});
    // Just to be sure.
    clearInterval(this.reconnectCountdown);
    this.clearDisconnectGraceTimer();
    this.handleError();

    const params = this.tinode.getServerInfo();
    this.setState({
      serverVersion: params.ver + ' ' + (params.build ? params.build : 'none'),
      // "reqCred":{"auth":["email"]}
      reqCredMethod: ((params.reqCred || {}).auth || [])[0] || 'email'
    });

    if (this.state.autoLogin) {
      this.doLogin(this.state.jwtToken, {meth: this.state.credMethod, resp: this.state.credCode});
    }
  }

  // Called for each auto-reconnect iteration.
  handleAutoreconnectIteration(sec, prom) {
    clearInterval(this.reconnectCountdown);

    // During grace, keep Slow Connection on ErrorPanel (no reconnect_countdown).
    if (this.disconnectGraceTimer) {
      return;
    }

    if (sec < 0) {
      // Clear error
      this.handleError();
      return;
    }

    if (prom) {
      // Reconnecting now
      prom.then(_ => {
        // Reconnected: clear error
        this.handleError();
      }).catch(err => {
        this.handleError(err.message, 'err');
      });
      return;
    }

    const {formatMessage} = this.props.intl;
    let count = sec / 1000;
    count = count | count;
    this.reconnectCountdown = setInterval(_ => {
      if (this.disconnectGraceTimer) {
        clearInterval(this.reconnectCountdown);
        return;
      }
      if (count < -10) {
        // Count could go to negative if computer woke up from sleep.
        clearInterval(this.reconnectCountdown);
        this.tinode.reconnect();
        return;
      }

      const fmtTime = (count > 99) ? secondsToTime(count) : count;
      this.handleError(
        formatMessage(messages.reconnect_countdown, {seconds: fmtTime}),
        'warn',
        _ => {
          clearInterval(this.reconnectCountdown);
          this.tinode.reconnect();
        },
        formatMessage(messages.reconnect_now)
      );
      count -= 1;
    }, 1000);
  }

  // Connection lost — wait for reconnect before showing a hard disconnect error.
  handleDisconnect(err) {
    bootWarn('net:disconnected', {code: err?.code, message: err?.message,
      myUserId: this.state.myUserId, chatRoomMode: this.state.chatRoomMode});
    clearInterval(this.reconnectCountdown);
    this.clearDisconnectGraceTimer();

    // Client-initiated disconnect: apply immediately.
    if (err?.code === 418) {
      this.setState({
        connected: false,
        ready: false,
        topicSelectedOnline: false,
        errorText: err.message || 'Disconnected',
        errorLevel: err.message ? 'err' : 'warn',
        errorId: null,
        loginDisabled: false,
        contextMenuVisible: false,
        forwardDialogVisible: false,
        serverVersion: 'no connection',
      });
      return;
    }

    this.setState({
      connected: false,
      ready: false,
      topicSelectedOnline: false,
      loginDisabled: false,
      serverVersion: 'no connection',
    });

    // Arm grace immediately so reconnect_countdown cannot overwrite Slow Connection.
    this.disconnectGraceTimer = setTimeout(() => {
      this.disconnectGraceTimer = null;
      if (this.tinode && this.tinode.isConnected()) {
        return;
      }
      const detail = err && err.message ? err.message : 'Disconnected';
      this.setState({
        connected: false,
        ready: false,
        topicSelectedOnline: false,
        errorText: detail,
        errorLevel: err && err.message ? 'err' : 'warn',
        errorId: null,
        loginDisabled: false,
        contextMenuVisible: false,
        forwardDialogVisible: false,
        serverVersion: 'no connection',
      });
      if (this.state.chatRoomMode) {
        // Routed through the shared signal so an established session stays
        // silent — the SDK keeps reconnecting and recovers on its own, whereas
        // fatal_error tells the host the embed is done for.
        this.signalChatLoadFailure(CHAT_ERROR_REASON.CONNECTION_FAILED);
      }
    }, DISCONNECT_GRACE_MS);

    this.showSlowConnectionBanner();
  }

  doLogin(jwtToken, cred) {
    bootLog('auth:doLogin', {hasJwt: !!jwtToken, alreadyAuthenticated: this.tinode.isAuthenticated(),
      hasSessionToken: !!(this.tinode.getAuthToken() || {}).token, myUserId: this.state.myUserId});
    if (this.tinode.isAuthenticated()) {
      this.clearAuthTimeout();
      this.pendingJwtLogin = false;
      // Credentials were never exercised: the session was already usable.
      settleNativeAuthSuccess(NATIVE_AUTH_STATUS.ALREADY_AUTHENTICATED);
      if (this.state.myUserId) {
        this.handleReconnectSuccessful();
      } else if (!this.state.chatRoomMode) {
        HashNavigation.navigateTo('');
      }
      return;
    }

    // Use JWT from mobile app, or fall back to a cached session token from a previous login.
    const sessionToken = (this.tinode.getAuthToken() || {}).token;
    if (!jwtToken && !sessionToken) {
      // No credentials available.
      this.clearAuthAttempt();
      if (this.state.chatRoomMode) {
        this.setState({authFailed: true});
        this.signalChatLoadFailure(CHAT_ERROR_REASON.AUTH_REJECTED);
        return;
      }
      HashNavigation.navigateTo('');
      return;
    }

    // Sanitize and package credential.
    cred = Tinode.credential(cred);
    if (jwtToken) {
      wsUpgradeJWT = jwtToken;
    } else {
      wsUpgradeJWT = '';
    }
    // May be disconnected.
    let connectionPromise = this.tinode.isConnected() ? Promise.resolve() : this.tinode.connect();
    let loginPromise;
    if (jwtToken) {
      // Primary path: authenticate using JWT provided by the mobile app.
      loginPromise = connectionPromise.then(_ => this.tinode.login('jwt', jwtToken, cred));
    } else {
      // Fallback: use session token from a previous login.
      loginPromise = connectionPromise.then(_ => this.tinode.loginToken(sessionToken, cred));
    }

    loginPromise.then(ctrl => {
      if (ctrl.code >= 300 && ctrl.text === 'validate credentials') {
        this.clearAuthTimeout();
        this.pendingJwtLogin = false;
        this.setState({loadSpinnerVisible: false, caxDaxPendingAuth: false, loginDisabled: false});
        if (this.state.chatRoomMode) {
          // WebView JWT flow cannot present a credential-validation screen; treat
          // as a failed load and notify the native host instead of navigating to
          // the (blank-for-host) credentials view.
          this.setState({authFailed: true});
          this.signalChatLoadFailure(CHAT_ERROR_REASON.AUTH_REJECTED);
          return;
        }
        if (cred) {
          this.handleError(this.props.intl.formatMessage(messages.code_doesnot_match), 'warn');
        }
        TinodeWeb.navigateToCredentialsView(ctrl.params);
      } else {
        this.handleLoginSuccessful();
      }
    }).catch(err => {
      const autoLogin = err.code >= 500;
      bootWarn('auth:login failed', {code: err?.code, message: err?.message,
        willRetry: autoLogin, chatRoomMode: this.state.chatRoomMode});
      // On retryable (5xx) failures keep the AUTH_TIMEOUT backstop running so a
      // persistent server error still ends in a fatal_error notification instead
      // of a silent blank screen while auto-login keeps retrying.
      this.clearAuthAttempt(autoLogin, autoLogin);
      // Connection or login failed, report error.
      this.setState({
        credMethod: undefined,
        credCode: undefined,
        ...(autoLogin || this.state.myUserId ? {} : {authFailed: true}),
      });
      this.handleError(err.message, 'err');
      console.warn("Login failed", err);
      if (!autoLogin) {
        if (this.retryWithPendingJwt(jwtToken)) {
          return;
        }
        this.signalChatLoadFailure(CHAT_ERROR_REASON.AUTH_REJECTED);
        if (this.state.chatRoomMode) {
          // Invalid URL JWT (often with a still-valid localStorage session): drop
          // saved login and stay blank for the host. Do not call handleLogout() —
          // that resets tinodeReady and sticks ChatLoadingView forever.
          TinodeWeb.clearSavedLogin();
          if (this.tinode) {
            this.tinode.setAuthToken(null);
          }
          return;
        }
        if (sessionToken) {
          this.handleLogout();
        }
        HashNavigation.navigateTo('');
      }
    });
  }

  static navigateToCredentialsView(params) {
    const parsed = HashNavigation.parseUrlHash(window.location.hash);
    parsed.path[0] = 'cred';
    parsed.params['method'] = params.cred[0];
    parsed.params['token'] = params.token;
    parsed.params['code'] = params.code;
    HashNavigation.navigateTo(HashNavigation.composeUrlHash(parsed.path, parsed.params));
  }

  bindMeTopicHandlers() {
    const me = this.tinode.getMeTopic();
    me.onMetaDesc = this.tnMeMetaDesc;
    me.onContactUpdate = this.tnMeContactUpdate;
    me.onSubsUpdated = this.tnMeSubsUpdated;
    return me;
  }

  refreshMeTopicSubscription() {
    const me = this.bindMeTopicHandlers();
    me.getMeta(
      me.startMetaQuery()
        .withDesc()
        .build()
    ).catch(err => {
      console.warn(`Failed to fetch me desc ${formatLogData({err})}`);
    });
    return me.subscribe(
      me.startMetaQuery().
        withLaterSub().
        withTags().
        withCred().
        build()
    ).catch(err => {
      // Don't disconnect the whole client if syncing 'me' metadata fails.
      this.handleError(err.message, 'err');
    });
  }

  handleReconnectSuccessful() {
    this.handleError();
    this.clearAuthTimeout();
    this.pendingJwtLogin = false;
    this.sessionExpiredSignaled = false;

    if (this.state.chatRoomMode || LocalStorageUtil.getObject('keep-logged-in')) {
      LocalStorageUtil.setObject('keep-logged-in', true);
      LocalStorageUtil.setObject('auth-token', this.tinode.getAuthToken());
    }

    this.refreshMeTopicSubscription();

    const embedHash = TinodeWeb.resolveEmbedHash(this.state.requestedHash);
    const patch = {
      connected: true,
      loadSpinnerVisible: false,
      loginDisabled: false,
      caxDaxPendingAuth: false,
      authFailed: false,
    };
    if (embedHash) {
      patch.requestedHash = embedHash;
    }
    this.setState(patch);
  }

  handleLoginSuccessful() {
    // Reached only when tinode.login() actually succeeded, so any pending host
    // request authenticated — including a re-auth that lands in the reconnect
    // branch below with myUserId still set from the replaced session.
    settleNativeAuthSuccess();
    this.sessionExpiredSignaled = false;

    // Same user as before: resume rather than re-initialize. A re-auth that
    // arrives with a token for a different user must fall through to the full
    // login below, or the previous user's state would be left on screen.
    if (this.state.myUserId && this.state.myUserId === this.tinode.getCurrentUserID()) {
      this.handleReconnectSuccessful();
      return;
    }

    this.handleError();
    this.clearAuthTimeout();
    this.pendingJwtLogin = false;

    // Perf: authentication complete (start of the login → topic stage).
    markOnce(PERF_MARKS.LOGIN_SUCCESS);

    // Save session for refresh; enable IndexedDB when keep-logged-in was not active yet.
    if (this.state.chatRoomMode || LocalStorageUtil.getObject('keep-logged-in')) {
      LocalStorageUtil.setObject('keep-logged-in', true);
      LocalStorageUtil.setObject('auth-token', this.tinode.getAuthToken());
      if (!this.state.persist) {
        this.tinode.initStorage().then(_ => {
          this.setState({persist: true});
        });
      }
    }

    const goToTopic = this.state.requestedTopic;
    this.bindMeTopicHandlers();
    // Reset badge readiness so the UI doesn't use stale permissions during relogin.
    this.setState({myTrustedBadgesReady: false});
    this.setState({
      connected: true,
      credMethod: undefined,
      credCode: undefined,
      credToken: undefined,
      myUserId: this.tinode.getCurrentUserID(),
      autoLogin: true,
      authFailed: false,
      caxDaxPendingAuth: false,
      requestedTopic: undefined,
      // Login is complete; any further sync should not block the UI.
      loadSpinnerVisible: false,
      loginDisabled: false,
    });
    this.refreshMeTopicSubscription();

    const embedHash = TinodeWeb.resolveEmbedHash(this.state.requestedHash);
    const isEmbedChatRoute = TinodeWeb.isEmbedRouteHash(embedHash);

    bootLog('auth:login success', {myUserId: this.tinode.getCurrentUserID(), goToTopic,
      embedHash, currentHash: globalThis.location.hash, isEmbedChatRoute});

    // Embedded cax/dax chat links go straight to the trip topic; staff/desktop goes to Find.
    if (isEmbedChatRoute) {
      if (embedHash !== globalThis.location.hash) {
        HashNavigation.navigateTo(embedHash);
      }
      this.setState({requestedHash: embedHash});
    } else {
      let urlHash = HashNavigation.setUrlSidePanel('', 'newtpk');
      if (goToTopic) {
        urlHash = HashNavigation.setUrlTopic(urlHash, goToTopic);
      }
      HashNavigation.navigateTo(urlHash);
    }
  }

  tnMeMetaDesc(desc) {
    if (desc) {
      // Mark trusted badge state as loaded once we receive 'me' description.
      // Trusted badges may arrive slightly after login; avoid showing "no access" placeholders prematurely.
      if (!this.state.myTrustedBadgesReady) {
        this.setState({myTrustedBadgesReady: true});
      }
      if (desc.public) {
        this.setState({
          sidePanelTitle: desc.public.fn,
          sidePanelAvatar: makeImageUrl(desc.public.photo)
        });
      }
      if (desc.trusted) {
        const badges = [];
        for (const [key, val] of Object.entries(desc.trusted)) {
          if (val) {
            badges.push(key);
          }
        }
        this.setState({
          myTrustedBadges: badges,
        });
      }
      if (desc.acs) {
        this.setState({
          incognitoMode: !desc.acs.isPresencer()
        });
      }
    }
  }

  // Reactions to updates to the contact list.
  tnMeContactUpdate(what, cont) {
    if (what == 'on' || what == 'off') {
      this.resetContactList();
      if (this.state.topicSelected == cont.topic) {
        this.setState({topicSelectedOnline: (what == 'on')});
      }
    } else if (what == 'read') {
      this.resetContactList();
    } else if (what == 'msg' && cont) {
      // Check if the topic is archived. If so, don't play a sound.
      const topic = this.tinode.getTopic(cont.topic);
      const archived = topic && topic.isArchived();

      // New message received. Maybe the message is from the current user, then unread is 0.
      if (cont.unread > 0 && this.state.messageSounds && !archived) {
        // Skip update if the topic is currently open, otherwise the badge will annoyingly flash.
        if (document.hidden || this.state.topicSelected != cont.topic) {
          POP_SOUND.play().catch(_ => {
            // play() throws if the user did not click the app first: https://goo.gl/xX8pDD.
          });
        }
      }
      // Reorder contact list to use possibly updated 'touched'.
      this.resetContactList();
    } else if (what == 'recv') {
      // Explicitly ignoring "recv" -- it causes no visible updates to contact list.
    } else if (what == 'gone' || what == 'unsub') {
      // Topic deleted or user unsubscribed. Remove topic from view.
      // If the currently selected topic is gone, clear the selection.
      if (this.state.topicSelected == cont.topic) {
        this.handleTopicSelected(null);
      }
      // Redraw without the deleted topic.
      this.resetContactList();
    } else if (what == 'acs') {
      // Permissions changed. If it's for the currently selected topic,
      // update the views.
      if (this.state.topicSelected == cont.topic) {
        this.setState({topicSelectedAcs: cont.acs});
      }
    } else if (what == 'del') {
      // TODO: messages deleted (hard or soft) -- update pill counter.
    } else if (what == 'upd' || what == 'call') {
      // upd, call - handled by the SDK. Explicitly ignoring here.
    } else {
      // TODO(gene): handle other types of notifications:
      // * ua -- user agent changes (maybe display a pictogram for mobile/desktop).
      console.info("Unsupported (yet) presence update:", what, "in", (cont || {}).topic);
    }
  }

  tnMeSubsUpdated(unused) {
    this.resetContactList();
  }

  // Merge search results and contact list to create a single flat
  // list of known contacts for GroupManager to use.
  static prepareSearchableContacts(chatList, foundContacts) {
    const merged = {};

    // For chatList topics merge only p2p topics and convert them to the
    // same format as foundContacts.
    for (const c of chatList) {
      if (Tinode.isP2PTopicName(c.topic)) {
          merged[c.topic] = {
            user: c.topic,
            updated: c.updated,
            public: c.public,
            private: c.private,
            acs: c.acs
          };
      }
    }

    // Add all foundCountacts if they have not been added already.
    for (const c of foundContacts) {
      if (!merged[c.user]) {
        merged[c.user] = c;
      }
    }

    return Object.values(merged);
  }

  resetContactList() {
    const newState = {
      chatList: []
    };

    if (!this.state.ready) {
      newState.ready = true;
    }

    this.tinode.getMeTopic().contacts((c) => {
      if (!c.topic && !c.user) {
        // Contacts expect c.topic to be set.
        c.topic = c.name;
      }

      newState.chatList.push(c);
      if (this.state.topicSelected == c.topic) {
        newState.topicSelectedOnline = c.online;
        newState.topicSelectedAcs = c.acs;
      }
    });

    const past = new Date(0);
    newState.chatList.sort((a, b) => {
      return (a.touched || past).getTime() - (b.touched || past).getTime();
    });

    // Merge search results and chat list.
    newState.searchableContacts = TinodeWeb.prepareSearchableContacts(newState.chatList, this.state.searchResults);
    this.setState(newState);
  }

  /* Fnd topic: find contacts by tokens */
  tnInitFind() {
    const fnd = this.tinode.getFndTopic();
    fnd.onSubsUpdated = this.tnFndSubsUpdated;
    if (fnd.isSubscribed()) {
      this.tnFndSubsUpdated();
    } else {
      fnd.subscribe(fnd.startMetaQuery().withSub().build()).catch(err => {
        this.handleError(err.message, 'err');
      });
    }
  }

  tnFndSubsUpdated(findQuery) {
    const foundContacts = [];
    // Don't attempt to create P2P topics which already exist. Server will reject the duplicates.
    this.tinode.getFndTopic().contacts((s) => {
      foundContacts.push(s);
    });
    const clearTopicOnEmptyFind = findQuery && foundContacts.length === 0 &&
      !this.state.forwardDialogVisible && this.state.topicSelected;
    this.setState({
      searchResults: foundContacts,
      searchableContacts: TinodeWeb.prepareSearchableContacts(this.state.chatList, foundContacts)
    }, _ => {
      if (clearTopicOnEmptyFind) {
        this.handleTopicSelected(null);
      }
    });
  }

  /** Called when the user enters a contact into the contact search field in the NewAccount panel
    @param query {Array} is an array of contacts to search for
   */
  handleSearchContacts(query) {
    const findQuery = Tinode.isNullValue(query) ? null : query;
    const fnd = this.tinode.getFndTopic();
    const ensureFndReady = fnd.isSubscribed() ?
      Promise.resolve() :
      fnd.subscribe(fnd.startMetaQuery().withSub().build());
    ensureFndReady
      .then(_ => fnd.setMeta({desc: {public: query}}))
      .then(_ => fnd.getMeta(fnd.startMetaQuery().withSub().build()))
      // Force a UI refresh even when server returns 304 "not modified".
      .then(_ => this.tnFndSubsUpdated(findQuery))
      .catch(err => this.handleError(err.message, 'err'));
  }

  // User clicked on a topic in the side panel or deleted a topic.
  handleTopicSelected(topicName) {
    // Clear newTopicParams after use.
    if (this.state.newTopicParams && this.state.newTopicParams._topicName != topicName) {
      this.setState({
        newTopicParams: null
      });
    }

    if (topicName) {
      this.setState({
        errorText: '',
        errorLevel: null,
        errorId: null,
        mobilePanel: 'topic-view',
        infoPanel: undefined
      });
      // Different topic selected.
      if (this.state.topicSelected != topicName) {
        this.setState({
          topicSelectedOnline: this.tinode.isTopicOnline(topicName),
          topicSelectedAcs: this.tinode.getTopicAccessMode(topicName),
          forwardMessage: null
        });
        this.handleHashRedirect(topicName);
      }
    } else {
      // Currently selected contact deleted
      this.setState({
        topicSelected: null,
        errorText: '',
        errorLevel: null,
        errorId: null,
        mobilePanel: 'sidepanel',
        topicSelectedOnline: false,
        topicSelectedAcs: null,
        infoPanel: undefined,
        forwardMessage: null
      });

      this.handleHashRedirect();
    }
  }

  handleHashRedirect(topicName = '') {
    const currentHash = window.location.hash;
    if (currentHash.startsWith('#dax')) {
      HashNavigation.navigateTo('#dax/' + topicName);
    } else if (currentHash.startsWith('#cax')) {
      HashNavigation.navigateTo('#cax/' + topicName);
    } else {
      HashNavigation.navigateTo(HashNavigation.setUrlTopic('', topicName || null));
    }
  }

  // In mobile view user requested to show sidepanel
  handleHideMessagesView() {
    this.setState({
      mobilePanel: 'sidepanel'
    });
    HashNavigation.navigateTo(HashNavigation.setUrlTopic(window.location.hash, null));
  }

  // User is sending a message, either plain text or a drafty object, possibly
  // with attachments.
  //  - msg - Drafty message with content
  //  - promise - Promise to be resolved when the upload is completed
  //  - uploadCompletionPromise - for tracking progress
  //  - head - head dictionary to be attached to the message
  handleSendMessage(msg, uploadCompletionPromise, uploader, head) {
    const topic = this.tinode.getTopic(this.state.topicSelected);
    /* TODO: check if return is required */
    return this.sendMessageToTopic(topic, msg, uploadCompletionPromise, uploader, head);
  }

  sendMessageToTopic(topic, msg, uploadCompletionPromise, uploader, head) {
    if (this.state.liveConnection === false) {
      this.handleError(this.props.intl.formatMessage(messages.no_connection), 'warn');
      return Promise.resolve();
    }
    msg = topic.createMessage(msg, false);
    // The uploader is used to show progress.
    msg._uploader = uploader;

    if (head) {
      msg.head = Object.assign(msg.head || {}, head);
    }

    const completion = [];
    if (uploadCompletionPromise) {
      completion.push(uploadCompletionPromise);
    }

    if (!topic.isSubscribed()) {
      // Topic is not subscribed yet. Subscribe.
      const subscribePromise =
        topic.subscribe()
          .then(_ => {
            // If there are unsent messages (except video call messages),
            // try sending them now. Unsent video call messages will be dropped.
            let calls = [];
            topic.queuedMessages(pub => {
              if (pub._sending || pub.seq == msg.seq) {
                return;
              }
              if (pub.head && pub.head.webrtc) {
                // Filter out unsent video call messages.
                calls.push(pub.seq);
                return;
              }
              if (topic.isSubscribed()) {
                topic.publishMessage(pub);
              }
            });
            if (calls.length > 0) {
              topic.delMessagesList(calls, true);
            }
          });
      completion.push(subscribePromise);
    }

    // TODO: check if return is required.
    return topic.publishDraft(msg, Promise.all(completion))
      .then(ctrl => {
        if (topic.isArchived()) {
          topic.archive(false);
        }
        return ctrl;
      })
      .catch(err => {
        // Only 422 is a permanent rejection (e.g. image moderation); override the SDK's
        // default "any 4xx" rule so other errors stay retryable.
        msg._fatal = err && err.code === 422;
        this.handleError(err.message, 'err');
      });
  }

  handleNewChatInvitation(topicName, action) {
    const topic = this.tinode.getTopic(topicName);
    let response = null;
    switch (action) {
      case 'accept':
        // Accept given permissions.
        const mode = topic.getAccessMode().getGiven();
        response = topic.setMeta({sub: {mode: mode}});
        if (topic.isP2PType()) {
          // For P2P topics change 'given' permission of the peer too.
          // In p2p topics the other user has the same name as the topic.
          response = response.then(_ => topic.setMeta({sub: {user: topicName, mode: mode}}));
        }
        break;
      case 'delete':
        // Ignore invitation by deleting it.
        response = topic.delTopic(true);
        break;
      case 'block':
        // Ban the topic making futher invites impossible.
        // Just self-ban.
        const am = topic.getAccessMode().updateWant('-JP').getWant();
        response = topic.setMeta({sub: {mode: am}}).then(_ => this.handleTopicSelected(null));
        break;
      default:
        console.warn("Unknown invitation action", '"' + action + '""');
    }

    if (response != null) {
      response.catch(err => this.handleError(err.message, 'err'));
    }
  }

  // Sign Up is disabled in this build.
  handleNewAccount() {
    // Registration disabled. No navigation, no UI.
  }

  // Account registration is disabled in this build.
  handleNewAccountRequest(_login, _password, _public, _cred, _tags) {
    console.warn("Account registration is disabled.");
  }

  handleToggleIncognitoMode(on) {
    // Make state undefined.
    this.setState({incognitoMode: null});

    const me = this.tinode.getMeTopic();
    const am = me.getAccessMode().updateWant(on ? '-P' : '+P').getWant();
    me.setMeta({sub: {mode: am}}).catch(err => {
      // Request failed, keep existing state.
      this.setState({incognitoMode: !on});
      this.handleError(err.message, 'err');
    });
  }

  handleUpdateAccountTagsRequest(_, tags) {
    this.tinode.getMeTopic().setMeta({tags: tags})
      .catch(err => this.handleError(err.message, 'err'));
  }

  // User updated global parameters.
  handleGlobalSettings(settings) {
    const serverAddress = settings.serverAddress || this.state.serverAddress;
    const transport = settings.transport || this.state.transport;
    const secureConnection = settings.secureConnection === undefined ?
      this.state.secureConnection : settings.secureConnection;
    if (this.tinode) {
      this.tinode.clearStorage();
      this.tinode.onDisconnect = undefined;
      this.tinode.disconnect();
    }
    this.tinode = TinodeWeb.tnSetup(serverAddress, secureConnection, transport,
      this.props.intl.locale, LocalStorageUtil.getObject('keep-logged-in'));
    this.tinode.onConnect = this.handleConnected;
    this.tinode.onDisconnect = this.handleDisconnect;
    this.tinode.onAutoreconnectIteration = this.handleAutoreconnectIteration;
    this.tinode.onInfoMessage = this.handleInfoMessage;
    this.tinode.onDataMessage = this.handleDataMessage;

    this.setState({
      serverAddress: serverAddress,
      transport: transport,
      secureConnection: secureConnection,
    });
    LocalStorageUtil.setObject('settings', {
      serverAddress: serverAddress,
      transport: transport,
      secureConnection: secureConnection,
    });

    HashNavigation.navigateTo(HashNavigation.setUrlSidePanel(window.location.hash, ''));
  }

  // User chose 'Archived chats'.
  handleShowArchive() {
    HashNavigation.navigateTo(HashNavigation.setUrlSidePanel(window.location.hash,
      this.state.myUserId ? 'archive' : ''));
  }

  // User viewes 'Blocked chats'.
  handleShowBlocked() {
    HashNavigation.navigateTo(HashNavigation.setUrlSidePanel(window.location.hash,
      this.state.myUserId ? 'blocked' : ''));
  }

  handleToggleMessageSounds(enabled) {
    this.setState({messageSounds: enabled});
    LocalStorageUtil.updateObject('settings', {
      messageSoundsOff: !enabled
    });
  }

  handleCredAdd(method, value) {
    const me = this.tinode.getMeTopic();
    me.setMeta({cred: {meth: method, val: value}})
      .catch(err => this.handleError(err.message, 'err'));
  }

  handleCredDelete(method, value) {
    const me = this.tinode.getMeTopic();
    me.delCredential(method, value)
      .catch(err => this.handleError(err.message, 'err'));
  }

  handleCredConfirm(method, response) {
    TinodeWeb.navigateToCredentialsView({cred: [method], code: response});
  }

  // User clicked Cancel button in Setting or Sign Up panel.
  handleSidepanelCancel() {
    const parsed = HashNavigation.parseUrlHash(window.location.hash);
    let path = '';
    if (['security','support','general','notif'].includes(parsed.path[0])) {
      path = 'edit';
    } else if ('crop' == parsed.path[0]) {
      path = 'general';
    } else if ('blocked' == parsed.path[0]) {
      path = 'security';
    } else if (this.state.myUserId) {
      path = 'newtpk';
    }
    parsed.path[0] = path;
    if (parsed.params) {
      delete parsed.params.code;
      delete parsed.params.method;
      delete parsed.params.tab;
      delete parsed.params.scheme;
      delete parsed.params.token;
    }
    HashNavigation.navigateTo(HashNavigation.composeUrlHash(parsed.path, parsed.params));
    this.setState({errorText: '', errorLevel: null});
  }

  // Sidepanel navigator. No need to bind to 'this'.
  basicNavigator(hash) {
    HashNavigation.navigateTo(HashNavigation.setUrlSidePanel(window.location.hash, hash));
  }

  // Topic info navigator. No need to bind to 'this'.
  infoNavigator(hash) {
    HashNavigation.navigateTo(HashNavigation.setUrlInfoPanel(window.location.hash, hash));
  }

  // Request to start a topic, new or selected from search results, or "by ID".
  handleStartTopicRequest(topicName, newTopicParams, isChannel) {
    // Check if topic is indeed new. If not, launch it.
    if (topicName && this.tinode.isTopicCached(topicName)) {
      this.handleTopicSelected(topicName);
      return;
    }

    const params = {};
    if (Tinode.isP2PTopicName(topicName)) {
      // Because we are initiating the subscription, set 'want' to all permissions.
      params.sub = {mode: DEFAULT_P2P_ACCESS_MODE};
      // Give the other user all permissions too.
      params.desc = {defacs: {auth: DEFAULT_P2P_ACCESS_MODE}};
    } else {
      topicName = topicName || this.tinode.newGroupTopicName(isChannel);
      if (newTopicParams) {
        params.desc = {public: newTopicParams.public, private: {comment: newTopicParams.private}};
        params.tags = newTopicParams.tags;
      }
    }
    params._topicName = topicName;
    this.setState({newTopicParams: params}, _ => {this.handleTopicSelected(topicName)});
  }

  // New topic was created, here is the new topic name.
  handleNewTopicCreated(oldName, newName) {
    let nextState = {};
    if (this.state.callShouldStart) {
      nextState = {callState: CALL_STATE_IN_PROGRESS, callShouldStart: false};
    }
    if (this.state.topicSelected == oldName && oldName != newName) {
      // If the current URl contains the old topic name, replace it with new.
      // Update the name of the selected topic first so the navigator doen't clear
      // the state.
      nextState.topicSelected = newName;
    }
    this.setState(nextState, _ => {
      this.handleHashRedirect(newName);
    });
  }

  handleTopicUpdateRequest(topicName, pub, priv, defacs) {
    this.handleError();

    const topic = this.tinode.getTopic(topicName);
    if (topic) {
      const params = {};
      let attachments;
      if (pub) {
        if (pub.photo) {
          if (pub.photo.ref && pub.photo.ref != Tinode.DEL_CHAR) {
            attachments = [pub.photo.ref];
          } else if (!pub.photo.data || pub.photo.data == Tinode.DEL_CHAR) {
            pub.photo = Tinode.DEL_CHAR;
          }
        }
        params.public = pub;
      }

      if (typeof priv == 'string') {
        params.private = (priv === Tinode.DEL_CHAR) ?
          Tinode.DEL_CHAR : {comment: priv};
      }
      if (defacs) {
        params.defacs = defacs;
      }
      topic.setMeta({desc: params, attachments: attachments})
        .catch(err => this.handleError(err.message, 'err'));
    }
  }

  handleUnarchive(topicName) {
    const topic = this.tinode.getTopic(topicName);
    if (topic) {
      topic.archive(false).catch(err => this.handleError(err.message, 'err'));
    }
  }

  handleUpdatePasswordRequest(password)  {
    this.handleError();

    if (password) {
      this.tinode.updateAccountBasic(null, this.tinode.getCurrentLogin(), password)
        .catch(err => this.handleError(err.message, 'err'));
    }
  }

  handleChangePermissions(topicName, mode, uid) {
    const topic = this.tinode.getTopic(topicName);
    if (topic) {
      const am = topic.getAccessMode();
      if (uid) {
        am.updateGiven(mode);
        mode = am.getGiven();
      } else {
        am.updateWant(mode);
        mode = am.getWant();
      }
      topic.setMeta({sub: {user: uid, mode: mode}})
        .catch(err => this.handleError(err.message, 'err'));
    }
  }

  handleTagsUpdateRequest(topicName, tags) {
    const topic = this.tinode.getTopic(topicName);
    if (topic) {
      topic.setMeta({tags: tags})
        .catch(err => this.handleError(err.message, 'err'));
    }
  }

  handleLogout() {
    wsUpgradeJWT = '';
    clearAdminParentMessengerToken();
    updateFavicon(0);

    // Remove stored data.
    localStorage.removeItem('auth-token');
    localStorage.removeItem('settings');

    clearInterval(this.reconnectCountdown);
    this.clearAuthTimeout();
    this.clearDisconnectGraceTimer();
    this.pendingJwtLogin = false;

    let cleared;
    if (this.tinode) {
      cleared = this.tinode.clearStorage();
      this.tinode.onDisconnect = undefined;
      this.tinode.disconnect();
    } else {
      cleared = Promise.resolve();
    }
    this.setState(this.getBlankState(), () => this.clearAuthAttempt());

    cleared.then(_ => {
      this.tinode = TinodeWeb.tnSetup(this.state.serverAddress, isSecureConnection(),
        this.state.transport, this.props.intl.locale, LocalStorageUtil.getObject('keep-logged-in'), _ => {
          this.tinode.onConnect = this.handleConnected;
          this.tinode.onDisconnect = this.handleDisconnect;
          this.tinode.onAutoreconnectIteration = this.handleAutoreconnectIteration;
          this.tinode.onInfoMessage = this.handleInfoMessage;
          this.tinode.onDataMessage = this.handleDataMessage;
          HashNavigation.navigateTo('');
        });
    });
  }

  handleDeleteAccount() {
    this.tinode.delCurrentUser(true).then(_ => {
      this.handleLogout();
    });
  }

  handleDeleteTopicRequest(topicName) {
    const topic = this.tinode.getTopic(topicName);
    if (!topic) {
      return;
    }

    // Request to hard-delete topic.
    topic.delTopic(true).then(_ => {
      // Hide MessagesView and InfoView panels.
      HashNavigation.navigateTo(HashNavigation.setUrlTopic(window.location.hash, ''));
    }).catch(err => {
      this.handleError(err.message, 'err');
    });
  }

  handleDeleteMessagesRequest(topicName) {
    const topic = this.tinode.getTopic(topicName);
    if (!topic) {
      return;
    }

    // Request hard-delete all messages.
    topic.delMessagesAll(true)
      .catch(err => this.handleError(err.message, 'err'));
  }

  handleLeaveUnsubRequest(topicName) {
    const topic = this.tinode.getTopic(topicName);
    if (!topic) {
      return;
    }

    topic.leave(true).then(_ => {
      // Hide MessagesView and InfoView panels.
      HashNavigation.navigateTo(HashNavigation.setUrlTopic(window.location.hash, ''));
    }).catch(err => {
      this.handleError(err.message, 'err');
    });
  }

  handleBlockTopicRequest(topicName) {
    const topic = this.tinode.getTopic(topicName);
    if (!topic) {
      return;
    }

    topic.updateMode(null, '-JP')
      .then(_ => {
        // Hide MessagesView and InfoView panels.
        HashNavigation.navigateTo(HashNavigation.setUrlTopic(window.location.hash, ''));
      })
      .catch(err => this.handleError(err.message, 'err'));
  }

  handleReportTopic(topicName) {
    const topic = this.tinode.getTopic(topicName);
    if (!topic) {
      return;
    }

    // Publish spam report.
    this.tinode.report('report', topicName);

    // Remove J and P permissions.
    topic.updateMode(null, '-JP')
    .then(_ => {
      // Hide MessagesView and InfoView panels.
      HashNavigation.navigateTo(HashNavigation.setUrlTopic(window.location.hash, ''));
    })
    .catch(err => this.handleError(err.message, 'err'));
  }

  handleShowContextMenu(params, menuItems) {
    this.setState({
      contextMenuVisible: true,
      contextMenuClickAt: {x: params.x, y: params.y},
      contextMenuParams: params,
      contextMenuItems: menuItems || this.defaultTopicContextMenu(params.topicName),
      contextMenuBounds: this.selfRef.current.getBoundingClientRect()
    });
  }

  //
  handleShowForwardDialog(params) {
    if (this.state.sidePanelSelected == 'newtpk') {
      // If the Find panel is active, close it.
      this.handleSidepanelCancel();
    }
    const header = '➦ ' + params.userName;
    const content = typeof params.content == 'string' ? Drafty.init(params.content) : Drafty.forwardedContent(params.content);
    const preview = Drafty.preview(content, FORWARDED_PREVIEW_LENGTH, true);
    const msg = Drafty.append(Drafty.appendLineBreak(Drafty.mention(header, params.userFrom)), content);
    const msgPreview = Drafty.quote(header, params.userFrom, preview);

    const head = {
      forwarded: params.topicName + ':' + params.seq
    };
    this.setState({
      forwardDialogVisible: true,
      forwardMessage: { head: head, msg: msg, preview: msgPreview }
    });
  }

  defaultTopicContextMenu(topicName) {
    const topic = this.tinode.getTopic(topicName);

    if (topic._deleted) {
      return [
        'topic_delete'
      ];
    }

    let muted = false, blocked = false, self_blocked = false, subscribed = false, deleter = false,
      archived = false, webrtc = false;
    if (topic) {
      subscribed = topic.isSubscribed();
      archived = topic.isArchived();

      const acs = topic.getAccessMode();
      if (acs) {
        muted = acs.isMuted();
        blocked = !acs.isJoiner();
        self_blocked = !acs.isJoiner('want');
        deleter = acs.isDeleter();
      }
    }

    webrtc = !!this.tinode.getServerParam('iceServers');

    return [
      subscribed ? {
        title: this.props.intl.formatMessage(messages.menu_item_info),
        handler: this.handleShowInfoView
      } : null,
      subscribed && Tinode.isP2PTopicName(topicName) && webrtc ? {
        title: this.props.intl.formatMessage(messages.menu_item_audio_call),
        handler: this.handleStartAudioCall
      } : null,
      subscribed && Tinode.isP2PTopicName(topicName) && webrtc ? {
        title: this.props.intl.formatMessage(messages.menu_item_video_call),
        handler: this.handleStartVideoCall
      } : null,
      subscribed ? 'messages_clear' : null,
      subscribed && deleter ? 'messages_clear_hard' : null,
      muted ? (blocked ? null : 'topic_unmute') : 'topic_mute',
      self_blocked ? 'topic_unblock' : 'topic_block',
      archived ? 'topic_restore' : 'topic_archive',
      'topic_delete'
    ];
  }

  handleHideContextMenu() {
    this.setState({
      contextMenuVisible: false,
      contextMenuClickAt: null,
      contextMenuParams: null,
      contextMenuBounds: null
    });
  }

  handleHideForwardDialog(keepForwardedMessage) {
    this.setState({
      forwardDialogVisible: false,
      forwardMessage: keepForwardedMessage ? this.state.forwardMessage : null
    });
  }

  handleContextMenuAction(action, promise, params) {
    if (action == 'topic_archive') {
      if (promise && params.topicName && params.topicName == this.state.topicSelected) {
        promise.then(_ => {
          this.handleTopicSelected(null);
        });
      }
    } else if (action == 'menu_item_forward') {
      this.handleShowForwardDialog(params);
    }
  }

  handleShowAlert(title, content, onConfirm, confirmText, onReject, rejectText) {
    this.setState({
      alertVisible: true,
      alertParams: {
        title: title,
        content: content,
        onConfirm: onConfirm,
        confirm: confirmText,
        onReject: onReject,
        reject: rejectText
      }
    });
  }

  handleShowInfoView() {
    HashNavigation.navigateTo(HashNavigation.addUrlParam(window.location.hash, 'info', 'info'));
    this.setState({infoPanel: 'info'});
  }

  handleMemberUpdateRequest(topicName, added, removed) {
    if (!topicName) {
      return;
    }

    const topic = this.tinode.getTopic(topicName);
    if (!topic) {
      return;
    }

    if (added && added.length > 0) {
      added.map((uid) => {
        topic.invite(uid, null)
          .catch(err => this.handleError(err.message, 'err'));
      });
    }

    if (removed && removed.length > 0) {
      removed.map((uid) => {
        topic.delSubscription(uid)
          .catch(err => this.handleError(err.message, 'err'));
      });
    }
  }

  handleValidateCredentialsRequest(cred, code, token) {
    if (this.tinode.isAuthenticated()) {
      // Adding new email or phone number in account setting.
      this.tinode.getMeTopic().setMeta({cred: {meth: cred, resp: code}})
        .then(_ => HashNavigation.navigateTo(HashNavigation.setUrlSidePanel(window.location.hash, 'newtpk')))
        .catch(err => this.handleError(err.message, 'err'));
    } else {
      // Credential validation on signup.
      this.setState({credMethod: cred, credCode: code, credToken: token});
      this.doLogin(token, {meth: cred, resp: code});
    }
  }

  handlePasswordResetRequest(method, value) {
    // If already connected, connnect() will return a resolved promise.
    return this.tinode.connect()
      .then(_ => this.tinode.requestResetAuthSecret('basic', method, value))
      .catch(err => {
        // Socket error
        this.handleError(err.message, 'err');
      });
  }

  handleResetPassword(newPassword, tempAuth) {
    const secret = base64ReEncode(tempAuth.secret);
    if (!secret || !tempAuth.scheme) {
      this.handleError(this.props.intl.formatMessage(messages.invalid_security_token), 'err');
    } else {
      this.tinode.connect()
        .then(_ => this.tinode.updateAccountBasic(null, null, newPassword, {scheme: tempAuth.scheme, secret: secret}))
        .then(_ => {
          this.handleError(this.props.intl.formatMessage(messages.password_reset_success), 'info');
          HashNavigation.navigateTo('');
        })
        .catch(err => {
          this.handleError(err.message, 'err');
        });
    }
  }

  handleShowCountrySelector(code, dial, selectedCallback) {
    this.handleShowAlert("Select country",
      <Suspense fallback={<div><FormattedMessage id="loading_note" defaultMessage="Loading..."
        description="Message shown when component is loading"/></div>}>
        <PhoneCountrySelector
          selected={code}
          onSubmit={(c, d) => {
            this.setState({alertVisible: false});
            selectedCallback(c, d);
          }} />
      </Suspense>,
      null, null, _ => {}, "Cancel");
  }

  handleStartVideoCall() {
    this.setState({
      callTopic: this.state.topicSelected,
      callState: CALL_STATE_OUTGOING_INITATED,
      callAudioOnly: false
    });
  }

  handleStartAudioCall() {
    this.setState({
      callTopic: this.state.topicSelected,
      callState: CALL_STATE_OUTGOING_INITATED,
      callAudioOnly: true
    });
  }

  handleCallInvite(callTopic, callSeq, callState, audioOnly) {
    switch (callState) {
      case CALL_STATE_OUTGOING_INITATED:
        const head = { webrtc: CALL_HEAD_STARTED, aonly: !!audioOnly };
        this.handleSendMessage(Drafty.videoCall(audioOnly), undefined, undefined, head)
          .then(ctrl => {
            if (ctrl.code < 200 || ctrl.code >= 300 || !ctrl.params || !ctrl.params.seq) {
              this.handleCallClose();
              return;
            }
            this.setState({callSeq: ctrl.params['seq']});
          });
        break;
      case CALL_STATE_IN_PROGRESS:
        const topic = this.tinode.getTopic(callTopic);
        if (!topic) {
          return;
        }
        // We've accepted the call. Let the other side know.
        topic.videoCall('accept', callSeq);
        break;
    }
  }

  handleCallRinging(callTopic, callSeq) {
    const topic = this.tinode.getTopic(callTopic);
    if (!topic) {
      return;
    }

    topic.videoCall('ringing', callSeq);
  }

  handleCallHangup(callTopic, callSeq) {
    const topic = this.tinode.getTopic(callTopic);
    if (!topic) {
      return;
    }

    topic.videoCall('hang-up', callSeq);
  }

  handleCallSendOffer(callTopic, callSeq, sdp) {
    const topic = this.tinode.getTopic(callTopic);
    if (!topic) {
      return;
    }

    topic.videoCall('offer', callSeq, sdp);
  }

  handleCallIceCandidate(callTopic, callSeq, candidate) {
    const topic = this.tinode.getTopic(callTopic);
    if (!topic) {
      return;
    }

    topic.videoCall('ice-candidate', callSeq, candidate);
  }
  handleCallSendAnswer(callTopic, callSeq, sdp) {
    const topic = this.tinode.getTopic(callTopic);
    if (!topic) {
      return;
    }

    topic.videoCall('answer', callSeq, sdp);
  }

  handleCallClose() {
    if (this.callTimeoutTimer) {
      clearTimeout(this.callTimeoutTimer);
    }
    this.setState({
      callTopic: undefined,
      callState: CALL_STATE_NONE,
      callAudioOnly: undefined
    });
  }

  handleCallAccept(topicName) {
    const topic = this.tinode.getTopic(topicName);
    if (!topic) {
      return;
    }
    if (topic.isSubscribed()) {
      this.handleTopicSelected(this.state.callTopic);
      this.setState({
        callState: CALL_STATE_IN_PROGRESS
      });
    } else {
      // We need to switch and subscribe to callTopic first.
      this.setState({
        callShouldStart: true,
      }, _ => this.handleTopicSelected(this.state.callTopic));
    }
  }

  handleInfoMessage(info) {
    if (info.what != 'call') {
      return;
    }
    switch (info.event) {
      case 'accept':
        // If another my session has accepted the call.
        if (Tinode.isMeTopicName(info.topic) && this.tinode.isMe(info.from)) {
          this.setState({
            callTopic: null,
            callState: CALL_STATE_NONE,
            callSeq: null,
            callAudioOnly: undefined
          });
          return;
        }
        if (info.topic == this.state.callTopic) {
          // Update state.
          this.setState({callState: CALL_STATE_IN_PROGRESS});
        }
        break;
      case 'hang-up':
        // Remote hangup.
        this.handleCallClose();
        break;
    }
  }

  handleDataMessage(data) {
    if (data.head && data.head.webrtc && data.head.webrtc == CALL_HEAD_STARTED) {
      // If it's a video call invite message.
      // See if we need to display incoming call UI.
      const topic = this.tinode.getTopic(data.topic);
      if (topic) {
        // Check if a later version of the message exists (which means the call
        // has been either accepted or finished already).
        const msg = topic.latestMsgVersion(data.seq) || data;
        if (msg.head && msg.head.webrtc && msg.head.webrtc == CALL_HEAD_STARTED) {
          // This is a legit new call.
          if (data.from != this.state.myUserId) {
            if (this.state.callState == CALL_STATE_NONE) {
              // Incoming call.
              this.setState({
                callTopic: data.topic,
                callState: CALL_STATE_INCOMING_RECEIVED,
                callSeq: data.seq,
                callAudioOnly: !!msg.head.aonly
              });
            } else {
              // Another call is either in progress or being established.
              // Reject the incoming call.
              this.handleCallHangup(data.topic, data.seq);
            }
          }
        }
      } else {
        console.warn("Received vc data message from unknown topic", data.topic);
      }
    }
  }

  renderChatRoomLoadingShell(content) {
    return (
      <div id="topic-view" className="chat-unauthorized-view">
        {content != null ?
          <div className="chat-unauthorized-content">
            {content}
          </div>
          : null}
      </div>
    );
  }

  // Reports, once per distinct state, whether the embed is currently rendering
  // nothing the user can see, and which condition is responsible.
  traceBlankRender(authInProgress) {
    if (!bootLogEnabled() || !this.state.chatRoomMode) {
      return;
    }
    let cause = null;
    if (!this.state.tinodeReady) {
      cause = 'tinodeReady=false (SDK setup has not finished; loading shell only)';
    } else if (!this.state.myUserId && !authInProgress) {
      cause = 'not authenticated and no auth in progress — MessagesView renders null on mobile';
    } else if (this.state.myUserId && !this.state.topicSelected) {
      cause = 'authenticated but topicSelected is empty — MessagesView renders an empty #topic-view';
    }

    const signature = `${cause}|${this.state.displayMobile}`;
    if (signature === this.lastBlankSignature) {
      return;
    }
    this.lastBlankSignature = signature;
    if (cause) {
      bootWarn('render:BLANK', {cause, authInProgress, authFailed: this.state.authFailed,
        myUserId: this.state.myUserId, topicSelected: this.state.topicSelected,
        tinodeReady: this.state.tinodeReady, connected: this.state.connected,
        displayMobile: this.state.displayMobile, hash: globalThis.location.hash});
    } else {
      bootLog('render:content visible', {myUserId: this.state.myUserId,
        topicSelected: this.state.topicSelected});
    }
  }

  render() {
    const authInProgress = this.isAuthInProgress();
    this.traceBlankRender(authInProgress);

    return (
      <div id="app-container" ref={this.selfRef}>
        {this.state.contextMenuVisible ?
          <ContextMenu
            tinode={this.tinode}
            bounds={this.state.contextMenuBounds}
            clickAt={this.state.contextMenuClickAt}
            params={this.state.contextMenuParams}
            items={this.state.contextMenuItems}
            hide={this.handleHideContextMenu}
            onShowAlert={this.handleShowAlert}
            onAction={this.handleContextMenuAction}
            onTopicRemoved={(topicName) => {
              if (topicName == this.state.topicSelected) {
                this.handleTopicSelected(null);
              }
            }}
            onError={this.handleError}
            liveConnection={this.state.liveConnection} />
          :
          null
        }
        {this.state.forwardDialogVisible ?
          <ForwardDialog
            tinode={this.tinode}
            contacts={this.state.chatList}
            topicSelected={this.state.topicSelected}
            myUserId={this.state.myUserId}

            hide={this.handleHideForwardDialog}
            onInitFind={this.tnInitFind}
            searchResults={this.state.searchResults}
            onSearchContacts={this.handleSearchContacts}
            onTopicSelected={this.handleStartTopicRequest}
          />
          :
          null
        }
        {this.state.callTopic && this.state.callState == CALL_STATE_INCOMING_RECEIVED ?
          <CallIncoming
            tinode={this.tinode}
            topic={this.state.callTopic}
            seq={this.state.callSeq}
            callState={this.state.callState}
            audioOnly={this.state.callAudioOnly}
            onClose={this.handleCallClose}
            onRinging={this.handleCallRinging}
            onAcceptCall={this.handleCallAccept}
            onReject={this.handleCallHangup}
            />
          :
          null
        }
        {this.state.alertVisible ?
          <Alert
            title={this.state.alertParams.title}
            content={this.state.alertParams.content}
            onReject={this.state.alertParams.onReject ? (_ => this.setState({alertVisible: false})) : null}
            reject={this.state.alertParams.reject}
            onConfirm={this.state.alertParams.onConfirm ?
              (_ => {this.setState({alertVisible: false}); this.state.alertParams.onConfirm();}) : null}
            confirm={this.state.alertParams.confirm}
            /> : null}

        {/* Chat room (cax/dax): loading from first paint; ChatRoomView as soon as SDK exists. */}
        {this.state.chatRoomMode ? (
          this.state.tinodeReady ? (
          <ChatRoomView
            tinode={this.tinode}
            connected={this.state.connected}
            ready={this.state.ready}
            online={this.state.topicSelectedOnline}
            acs={this.state.topicSelectedAcs}
            displayMobile={this.state.displayMobile}
            viewportWidth={this.state.viewportWidth}
            viewportHeight={this.state.viewportHeight}
            topic={this.state.topicSelected}
            myUserId={this.state.myUserId}
            authInProgress={authInProgress}
            authFailed={this.state.authFailed}
            myUserName={this.state.sidePanelTitle}
            serverVersion={this.state.serverVersion}
            serverAddress={this.state.serverAddress}
            applicationVisible={this.state.applicationVisible}

            forwardMessage={this.state.forwardMessage}
            onCancelForwardMessage={this.handleHideForwardDialog}

            callTopic={this.state.callTopic}
            callSeq={this.state.callSeq}
            callState={this.state.callState}
            callAudioOnly={this.state.callAudioOnly}
            onCallHangup={this.handleCallHangup}

            onCallInvite={this.handleCallInvite}
            onCallSendOffer={this.handleCallSendOffer}
            onCallIceCandidate={this.handleCallIceCandidate}
            onCallSendAnswer={this.handleCallSendAnswer}

            errorText={this.state.errorText}
            errorLevel={this.state.errorLevel}
            errorAction={this.state.errorAction}
            errorActionText={this.state.errorActionText}
            errorId={this.state.errorId}

            newTopicParams={this.state.newTopicParams}

            onHideMessagesView={this.handleHideMessagesView}
            onError={this.handleError}
            onNewTopicCreated={this.handleNewTopicCreated}
            showContextMenu={this.handleShowContextMenu}
            onChangePermissions={this.handleChangePermissions}
            onNewChat={this.handleNewChatInvitation}
            sendMessage={this.handleSendMessage}
            onVideoCallClosed={this.handleCallClose}
            chatRoomMode={this.state.chatRoomMode}
            liveConnection={this.state.liveConnection}
            onCheckNSFWContent={checkNSFWContent}
            onWarmNSFW={loadNSFWModel} />
          ) : (
            this.renderChatRoomLoadingShell(<ChatLoadingView />)
          )
        ) : (
          <>
            {!this.state.displayMobile || this.state.mobilePanel == 'sidepanel' ?
              <SidepanelView
                tinode={this.tinode}
                connected={this.state.connected}
                displayMobile={this.state.displayMobile}
                state={this.state.sidePanelSelected}
                title={this.state.sidePanelTitle}
                avatar={this.state.sidePanelAvatar}
                trustedBadges={this.state.myTrustedBadges}
                trustedBadgesReady={this.state.myTrustedBadgesReady}
                myUserId={this.state.myUserId}
                authInProgress={authInProgress}

                errorText={this.state.errorText}
                errorLevel={this.state.errorLevel}
                errorAction={this.state.errorAction}
                errorActionText={this.state.errorActionText}
                errorId={this.state.errorId}
                topicSelected={this.state.topicSelected}
                chatList={this.state.chatList}
                credMethod={this.state.credMethod}
                credCode={this.state.credCode}
                credToken={this.state.credToken}

                transport={this.state.transport}
                messageSounds={this.state.messageSounds}
                incognitoMode={this.state.incognitoMode}
                serverAddress={this.state.serverAddress}
                secureConnection={this.state.secureConnection}
                serverVersion={this.state.serverVersion}
                reqCredMethod={this.state.reqCredMethod}

                onGlobalSettings={this.handleGlobalSettings}
                onSettings={this.handleSettings}
                onNavigate={this.basicNavigator}
                onUpdateAccountDesc={this.handleTopicUpdateRequest}
                onUpdatePassword={this.handleUpdatePasswordRequest}
                onUpdateAccountTags={this.handleUpdateAccountTagsRequest}
                onToggleMessageSounds={this.handleToggleMessageSounds}
                onToggleIncognitoMode={this.handleToggleIncognitoMode}
                onCredAdd={this.handleCredAdd}
                onCredDelete={this.handleCredDelete}
                onCredConfirm={this.handleCredConfirm}
                onTopicSelected={this.handleTopicSelected}
                onCreateTopic={this.handleStartTopicRequest}
                onLogout={this.handleLogout}
                onDeleteAccount={this.handleDeleteAccount}
                onShowAlert={this.handleShowAlert}
                onCancel={this.handleSidepanelCancel}
                onError={this.handleError}
                onValidateCredentials={this.handleValidateCredentialsRequest}
                onPasswordResetRequest={this.handlePasswordResetRequest}
                onResetPassword={this.handleResetPassword}
                onShowArchive={this.handleShowArchive}
                onShowBlocked={this.handleShowBlocked}
                onShowCountrySelector={this.handleShowCountrySelector}

                onInitFind={this.tnInitFind}
                searchResults={this.state.searchResults}
                onSearchContacts={this.handleSearchContacts}

                showContextMenu={this.handleShowContextMenu} />
              : null}

            {!this.state.displayMobile || (this.state.mobilePanel == 'topic-view' && !this.state.infoPanel) ?
              (this.state.tinodeReady ?
              <MessagesView
                tinode={this.tinode}
                connected={this.state.connected}
                ready={this.state.ready}
                online={this.state.topicSelectedOnline}
                acs={this.state.topicSelectedAcs}
                displayMobile={this.state.displayMobile}
                viewportWidth={this.state.viewportWidth}
                viewportHeight={this.state.viewportHeight}
                topic={this.state.topicSelected}
                myUserId={this.state.myUserId}
                authInProgress={authInProgress}
                // User public.fn.
                myUserName={this.state.sidePanelTitle}
                serverVersion={this.state.serverVersion}
                serverAddress={this.state.serverAddress}
                applicationVisible={this.state.applicationVisible}

                forwardMessage={this.state.forwardMessage}
                onCancelForwardMessage={this.handleHideForwardDialog}

                callTopic={this.state.callTopic}
                callSeq={this.state.callSeq}
                callState={this.state.callState}
                callAudioOnly={this.state.callAudioOnly}
                onCallHangup={this.handleCallHangup}

                onCallInvite={this.handleCallInvite}
                onCallSendOffer={this.handleCallSendOffer}
                onCallIceCandidate={this.handleCallIceCandidate}
                onCallSendAnswer={this.handleCallSendAnswer}

                errorText={this.state.errorText}
                errorLevel={this.state.errorLevel}
                errorAction={this.state.errorAction}
                errorActionText={this.state.errorActionText}
                errorId={this.state.errorId}

                newTopicParams={this.state.newTopicParams}

                onHideMessagesView={this.handleHideMessagesView}
                onError={this.handleError}
                onNewTopicCreated={this.handleNewTopicCreated}
                showContextMenu={this.handleShowContextMenu}
                onChangePermissions={this.handleChangePermissions}
                onNewChat={this.handleNewChatInvitation}
                sendMessage={this.handleSendMessage}
                onVideoCallClosed={this.handleCallClose}
                onCheckNSFWContent={checkNSFWContent}
                onWarmNSFW={loadNSFWModel}
                liveConnection={this.state.liveConnection} />
              : null) : null}
          </>
        )}

        {this.state.infoPanel && !this.state.chatRoomMode && this.state.tinodeReady ?
          <InfoView
            tinode={this.tinode}
            connected={this.state.connected}
            displayMobile={this.state.displayMobile}
            topic={this.state.topicSelected}
            searchableContacts={this.state.searchableContacts}
            myUserId={this.state.myUserId}
            panel={this.state.infoPanel}

            errorText={this.state.errorText}
            errorLevel={this.state.errorLevel}
            errorAction={this.state.errorAction}
            errorActionText={this.state.errorActionText}
            errorId={this.state.errorId}
            onNavigate={this.infoNavigator}
            onTopicDescUpdateRequest={this.handleTopicUpdateRequest}
            onShowAlert={this.handleShowAlert}
            onChangePermissions={this.handleChangePermissions}
            onMemberUpdateRequest={this.handleMemberUpdateRequest}
            onDeleteTopic={this.handleDeleteTopicRequest}
            onDeleteMessages={this.handleDeleteMessagesRequest}
            onLeaveTopic={this.handleLeaveUnsubRequest}
            onBlockTopic={this.handleBlockTopicRequest}
            onReportTopic={this.handleReportTopic}
            onAddMember={this.handleManageGroupMembers}
            onTopicTagsUpdateRequest={this.handleTagsUpdateRequest}
            onTopicUnArchive={this.handleUnarchive}
            onInitFind={this.tnInitFind}
            onError={this.handleError}

            showContextMenu={this.handleShowContextMenu}
            />
          :
          null
        }
      </div>
    );
  }
};

export default injectIntl(TinodeWeb);
